import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";
import handleErrorForThunk from "helpers/handleErrorForThunk";
import { parseServerError } from "utils/errors";
import { taxSettingStatusToUiString } from "utils/taxSettingEntityStatus";
import isEqual from '../../../containers/EPRReporting/Subcategories/Step_3/helper'
import { editActivateEpr } from "../manager/managerSlice";


const initialState = {
  declarations: { items: [] },
  declarationsParams: {},
  declarationsTransactions: [],
  returnPreview: {},
  liabilitiesAndObligations: [],
  liabilities: [],
  obligations: [],
  typesOfReport: [],
  checkedNumbers: {
    correct: [],
    incorrect: [],
    pending: [],
  },
  declarationsSalesTax: { items: [] },
  taxPermits: [],
  filtersEPR: {},
  listEPR: [],
  eprData: [],
  eprDeclaration: {},
  eprCategories: [],
  eprSubcategories: {
    selectedCategory1: [],
    selectedCategory2: [],
    selectedCategory3: [],
    selectedCategory4: [],
    selectedCategory5: [],
    selectedCategory6: [],
    selectedCategory7: [],
    selectedCategory8: [],
    quantityArr: [],
    unitsDefault: '',
    // New isolated approach for NLD Batteries with objects and unique IDs
    nldBatteriesItems: []
  },
  eprFurniture: {
    categories: null,
    products: null,
    materials: null,
    features: null,
  },
  eprCountries: [],
  declarationsEpr: { items: [] },
  dac7: null,
  user_status: [],
  dac7Reports: [],
  dac7ReportsImmovable: [],
  dac7Preview: [],
  relevant_activities: [],
  dac7_files_examples: [],
  furnitureChoice: {},
  statuses: [],
  report1099: null,
  creating1099Report: false,
  creating1099ReportError: null,
  dataVatReturn: {},
  creatingVatReturn: {},
  dataForDac7ReportInfo: {},
  dac7ReportId: null,
  dataCreateEprDeclaration: {},
  changeVariantCreatedDeclarationEpr: 'manual',
  createdSalesTaxReturn: null,
  recalculatedSalesTaxData: null,
  fetchingRecalculatedData: false,
  fetchingRecalculatedDataError: null,
  /** Sales tax return transactions (GET /returns/sales_tax_returns/transaction) — separate from VAT declarationsTransactions */
  salesTaxReturnTransactions: { items: [], count: 0 },
  loadingSalesTaxReturnTransaction: false,
  salesTaxReturnTransactionError: null,
  recalculatingSalesTaxReturnTransactions: false,
  recalculateSalesTaxReturnTransactionsError: null,
  creatingSalesTaxReturnTransaction: false,
  createSalesTaxReturnTransactionError: null,
  voidingSalesTaxReturnTransaction: false,
  voidSalesTaxReturnTransactionError: null,
  /** Input VAT import transactions (GET/POST /returns/transactions) — separate from declarations/transaction */
  returnsTransactions: { items: [], count: 0 },
  loadingReturnsTransactions: false,
  returnsTransactionsError: null,
  recalculatingReturnsTransactions: false,
  recalculateReturnsTransactionsError: null,
  creatingReturnsTransaction: false,
  createReturnsTransactionError: null,
  editingReturnsTransaction: false,
  editReturnsTransactionError: null,
};

function resolveEprStatus(rawStatus) {
  return taxSettingStatusToUiString(rawStatus);
}

function resolveEprActivate(rawActivate, status) {
  if (typeof rawActivate === "boolean") return rawActivate;
  return status === "active";
}

export const create1099Report = createAsyncThunk(
  "reports/create1099Report",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(`/reports/1099`, "POST", data);
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchEprStatus = createAsyncThunk(
  "reports/fetchEprStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(`/update/status_epr`, "GET");
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const updateEpr = createAsyncThunk(
  "reports/updateEpr",
  async ({ period, period_type, year }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/update_epr_by_date?period=${period}&period_type=${period_type}&year=${year}`,
        "POST"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteEPRReport = createAsyncThunk(
  "reports/deleteEPRReport",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/declarations/EPR_report?report_id=${id}`,
        "DELETE"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const updateEprEcoFee = createAsyncThunk(
  "reports/updateEprEcoFee",
  async ({ id, eco_fee_due, eco_fee_due_date, currency_id }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/epr_reporting/eco_fee_due",
        "PUT",
        { id, eco_fee_due, eco_fee_due_date, currency_id }
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const uploadMerchantsFile = createAsyncThunk(
  "reports/uploadMerchantsFile",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await callApi("/reports/upload_merchant", "POST", formData, {
        contentType: "multipart/form-data",
      });
      if (!response.ok) {
        // Try to extract error details from response
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          // If JSON parsing fails, return status as error
          return rejectWithValue({ message: `Server error: ${response.status}` });
        }
        
        // Check if error has detail array (FastAPI format)
        if (errorData?.detail && Array.isArray(errorData.detail)) {
          return rejectWithValue(errorData.detail);
        } else if (errorData?.detail) {
          return rejectWithValue(errorData.detail);
        } else if (errorData?.message) {
          return rejectWithValue(errorData.message);
        } else if (errorData?.error) {
          return rejectWithValue(errorData.error);
        } else {
          return rejectWithValue(errorData || { message: `Server error: ${response.status}` });
        }
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

// export const uploadMerchantsFileEprReporting = createAsyncThunk(
//   "reports/uploadMerchantsFileEprReporting",
//   async (file, { rejectWithValue }) => {
//     try {
//       const response = await callApi("/epr_reporting/import", "POST", file, {
//         contentType: "multipart/form-data",
//       });
//
//       if (!response.ok) {
//         const errorData = await response.json();
//         console.log('errorData', errorData)
//         // Format errors into a string
//         const formattedErrors = ['Validation errors in uploaded data:'];
//         const errors = Object.entries(errorData);
//         errors.forEach(([line, error], index) => {
//           formattedErrors.push(`***${index + 1}. Error in line ${error.line}: ${error.error}`).join('\n');
//         });
//         return rejectWithValue({
//           success: false,
//           errors: formattedErrors.join('\n'),
//           message: 'Validation errors in uploaded data'
//         });
//       }
//
//       return response.json();
//     } catch (err) {
//       console.log('err', err);
//       return rejectWithValue({
//         success: false,
//         message: 'Error uploading file'
//       });
//     }
//   }
// );
export const uploadMerchantsFileEprReporting = createAsyncThunk(
  "reports/uploadMerchantsFileEprReporting",
  async (file, { rejectWithValue }) => {
    try {
      const response = await callApi("/epr_transactions/import", "POST", file, {
        contentType: "multipart/form-data",
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        // console.log('errorData', errorData);
        
        		// Now just return data as is
        return rejectWithValue({
          success: false,
          errors: errorData,
          message: 'Validation errors in uploaded data'
        });
      }
      
      return response.json();
    } catch (err) {
      // console.log('err', err);
      return rejectWithValue({
        success: false,
        message: 'Error uploading file'
      });
    }
  }
);

export const addMerchantDac7 = createAsyncThunk(
  "reports/addMerchantDac7",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(`/reports/merchant`, "POST", data);
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const submitDac7Report = createAsyncThunk(
  "reports/submitDac7Report",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(`/reports/dac_7/submit?id=${id}`, "PUT");
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const getDac7Preview = createAsyncThunk(
  "reports/getDac7Preview",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(`/reports/dac_7/view?id=${id}`, "GET");
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const changeDac7Status = createAsyncThunk(
  "reports/changeDac7Status",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/reports/dac_7/status?id=${id}&status=${status}`,
        "PUT"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteDac7Report = createAsyncThunk(
  "reports/deleteDac7Report",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(`/reports/dac_7?id=${id}`, "DELETE");
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const downloadDac7File = createAsyncThunk(
  "reports/downloadDac7File",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/reports/dac_7/download?id=${id}`,
        "GET",
        {},
        {
          responseType: "blob",
        }
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      return { link: url };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const sendDac7Report = createAsyncThunk(
  "reports/sendDac7Report",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(`/reports/dac_7`, "POST", data);
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchDac7Reports = createAsyncThunk(
  "reports/fetchDac7Reports",
  async (data = { is_immovable: false }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/reports/dac_7?is_immovable=${data.is_immovable}`,
        "GET"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const editMerchantInfo = createAsyncThunk(
  "reports/editMerchantInfo",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(`/reports/merchant_info`, "PUT", data);
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createDac7Report = createAsyncThunk(
  "reports/createDac7Report",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(`/reports/dac_7`, "POST", data);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText ?? "Unknown error occurred");
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(handleErrorForThunk(err));
    }
  }
);

export const downloadEprFile = createAsyncThunk(
  "reports/downloadEprFile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/epr_reporting/epr_report_download/${id}`,
        "GET",
        {},
        {
          responseType: "blob",
        }
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      return { link: url };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const submitEprReport = createAsyncThunk(
  "reports/submitEprReport",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(`/epr_reporting/send_epr_report`, "POST", {
        id,
      });
      if (!response.ok) {
        let errBody = {};
        try {
          errBody = await response.json();
        } catch {
          errBody = { error: await response.text() || String(response.status) };
        }
        const message = errBody?.error || errBody?.message || String(response.status);
        return rejectWithValue({ error: message, status: response.status });
      }
      return response.json();
    } catch (err) {
      return rejectWithValue({ error: err?.message || "Request failed" });
    }
  }
);

export const fetchPreviewEpr = createAsyncThunk(
  "reports/fetchPreviewEpr",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/epr_reporting/preview/${id}`,
        "GET"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchEprDeclarations = createAsyncThunk(
  "epr/fetchEprDeclarations",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi(`/epr_reporting`, "GET", params);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(handleErrorForThunk(err));
    }
  }
);

export const fetchEprCountries = createAsyncThunk(
  "epr/fetchEprCountries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(`/subscription/epr_countries`, "GET");
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);


export const editTransaction = createAsyncThunk(
  "epr/editTransaction",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/declarations/edit_transaction`,
        "POST",
        data
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchEprFurnitureChoice = createAsyncThunk(
  "epr/fetchEprFurnitureChoice",
  async (code, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/epr_calc/get_epr_furniture_choice?code=${code}`,
        "GET"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchEprFurnitureFeatures = createAsyncThunk(
  "epr/fetchEprFurnitureFeatures",
  async (
    { country_code, category_code, product_code, material_code },
    { rejectWithValue }
  ) => {
    try {
      const response = await callApi(
        `/epr_calc/get_epr_furniture_features?category_code=${category_code}&product_code=${product_code}&matereal_code=${material_code}&country_code=${country_code}`,
        "GET"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchEprFurnitureMaterials = createAsyncThunk(
  "epr/fetchEprFurnitureMaterials",
  async (
    { country_code, category_code, product_code },
    { rejectWithValue }
  ) => {
    try {
      const response = await callApi(
        `/epr_calc/get_epr_furniture_matereals?category_code=${category_code}&product_code=${product_code}&country_code=${country_code}`,
        "GET"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchEprFurnitureProducts = createAsyncThunk(
  "epr/fetchEprFurnitureProducts",
  async ({ country_code, category_code }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/epr_calc/get_epr_furniture_products?category_code=${category_code}&country_code=${country_code}`,
        "GET"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchEprFurnitureCategories = createAsyncThunk(
  "epr/fetchEprFurnitureCategories",
  async (country_code, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/epr_calc/get_epr_furniture_categories?country_code=${country_code}`,
        "GET"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const downloadMemoFile = createAsyncThunk(
  "epr/downloadMemoFile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/declarations/download_memo/${id}`,
        "GET",
        {},
        {
          responseType: "blob",
        }
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      return { link: url };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchSalesTaxPermits = createAsyncThunk(
  "epr/fetchSalesTaxPermits",
  async (active = false, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/sales_tax_permit?is_active=${active}`,
        "GET"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);
export const createDeclarationSalesTax = createAsyncThunk(
  "epr/createDeclarationSalesTax",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/sales_tax_returns`,
        "POST",
        params
      );
      const responseData = await response.json();
      
      if (!response.ok) {
        // For 403 errors, we still want to return the data if it contains subscription info
        if (response.status === 403 && responseData && (responseData.to_buy || responseData.message)) {
          return responseData;
        }
        // Return full error object with status so UI can show 403 server message in banner
        return rejectWithValue({ ...responseData, status: response.status });
      }
      
      return responseData;
    } catch (err) {
      return rejectWithValue({ error: err?.message, status: err?.status });
    }
  }
);

export const fetchRecalculatedSalesTaxData = createAsyncThunk(
  "salesTax/fetchRecalculatedSalesTaxData",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/sales_tax_returns`,
        "POST",
        params
      );
      if (!response.ok) {
        const json = await response.json();
        throw new Error(json.error || 'Failed to fetch recalculated data');
      }
      
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const getSalesTaxPreview = createAsyncThunk(
  "salesTax/getSalesTaxPreview",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/sales_tax_returns/preview_sales_tax_return?sales_tax_return_id=${id}`,
        "POST"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchDeclarationsSalesTax = createAsyncThunk(
  "salesTax/fetchDeclarationsSalesTax",
  async (params = { limit: 25, offset: 0 }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/returns/sales_tax_returns",
        "GET",
        params
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(handleErrorForThunk(err));
    }
  }
);

export const submitSalesTax = createAsyncThunk(
  "salesTax/submitSalesTax",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/returns/sales_tax_returns/send",
        "POST",
        { id }
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

async function readSalesTaxDownloadSchedulesErrorMessage(response) {
  try {
    const text = await response.clone().text();
    if (!text?.trim()) return null;
    try {
      const parsed = JSON.parse(text);
      if (parsed?.error != null) {
        return typeof parsed.error === "string" ? parsed.error : JSON.stringify(parsed.error);
      }
      if (parsed?.message != null) return String(parsed.message);
      if (parsed?.detail != null) {
        return typeof parsed.detail === "string" ? parsed.detail : JSON.stringify(parsed.detail);
      }
    } catch {
      return text;
    }
  } catch {
    return null;
  }
  return null;
}

export const downloadSchedulesFile = createAsyncThunk(
  "salesTax/downloadSchedulesFile",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/sales_tax_returns/download_schedules`,
        "POST",
        data,
        {
          responseType: "blob",
        }
      );
      if (!response.ok) {
        const serverMessage = await readSalesTaxDownloadSchedulesErrorMessage(response);
        return rejectWithValue(
          serverMessage || `Request failed (${response.status})`
        );
      }
      const contentType = (response.headers.get("content-type") || "").toLowerCase();
      if (contentType.includes("application/json") || contentType.includes("text/plain")) {
        const serverMessage = await readSalesTaxDownloadSchedulesErrorMessage(response);
        return rejectWithValue(serverMessage || "Download failed");
      }
      const blob = await response.blob();
      if (!blob.size) {
        return rejectWithValue("File is empty");
      }
      const url = window.URL.createObjectURL(blob);
      return { link: url };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteSalesTax = createAsyncThunk(
  "salesTax/deleteSalesTax",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/sales_tax_returns/delete_sales_tax_return?sales_tax_return_id=${id}`,
        "DELETE"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const downloadDeclarationFile = createAsyncThunk(
  "salesTax/downloadDeclarationFile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/declarations/download/${id}`,
        "GET",
        {},
        {
          responseType: "blob",
        }
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      return { link: url };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createEprFurnitureEcoCode = createAsyncThunk(
  "epr/createEprFurnitureEcoCode",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/epr_reporting/post_epr_furniture_eco_code`,
        "POST",
        data
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const checkVatNumbers = createAsyncThunk(
  "epr/checkVatNumbers",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/declarations/check_vat_number`,
        "POST",
        data
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchTypesOfReport = createAsyncThunk(
  "declarations/fetchTypesOfReport",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/returns/declarations/types_of_report",
        "GET"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createDeclaration = createAsyncThunk(
  "declarations/createDeclaration",
  async (params, { rejectWithValue }) => {
    try {
      const response = await callApi(`/returns/declarations`, "POST", params);
      const responseData = await response.json();

      if (!response.ok) {
        // For 403 errors, we still want to return the data if it contains subscription info
        if (response.status === 403 && responseData && (responseData.to_buy || responseData.message)) {
          return responseData;
        }
        // Return full error object with status so UI can show 403 server message in banner
        return rejectWithValue({ ...responseData, status: response.status });
      }

      return responseData;
    } catch (err) {
      return rejectWithValue({ error: err?.message, status: err?.status });
    }
  }
);

export const createEprDeclaration = createAsyncThunk(
  "epr/createEprDeclaration",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/epr_reporting/create_epr_declaration`,
        "POST",
        data
      );
      const responseData = await response.json();
      
      if (!response.ok) {
        // For 403 errors, we still want to return the data if it contains subscription info
        if (response.status === 403 && responseData && (responseData.to_buy || responseData.message)) {
          return responseData;
        }
        // Return full error object (e.g., {error: "country not supported"})
        return rejectWithValue(responseData);
      }
      
      return responseData;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteDeclaration = createAsyncThunk(
  "declarations/deleteDeclaration",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/declarations?declaration_id=${id}`,
        "DELETE"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const getReturnPreview = createAsyncThunk(
  "declarations/getReturnPreview",
  async (id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/declarations/preview/${id}`,
        "GET"
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const submitDeclaration = createAsyncThunk(
  "declarations/submitDeclaration",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/declarations/send_declaration`,
        "POST",
        data
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText ?? "Unknown error occurred");
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchListEPR = createAsyncThunk(
  "epr/fetchListEPR",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/epr_settings?is_active=false", "GET");
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchFiltersEPR = createAsyncThunk(
  "epr/fetchFiltersEPR",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/subscription/epr_categories", "GET");
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createSystemOperator = createAsyncThunk(
  "operators/createSystemOperator",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/returns/declarations/epr_operator",
        "POST",
        data
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const getObligations = createAsyncThunk(
  "obligations/getObligations",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/returns/declarations/obligations",
        "POST",
        { ...data }
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const getLiabilities = createAsyncThunk(
  "liabilities/getLiabilities",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/returns/declarations/liabilities",
        "POST",
        { ...data }
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchDeclarations = createAsyncThunk(
  "declarations/fetchDeclarations",
  async (params = { limit: 25, offset: 0 }, { rejectWithValue }) => {
    try {
      const response = await callApi("/returns/declarations", "GET", params);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(handleErrorForThunk(err));
    }
  }
);

export const createEPRSettings = createAsyncThunk(
  "epr/createEPRSettings",
  async (data, { rejectWithValue }) => {
    const raw = data && typeof data === "object" ? data : {};
    const _display = raw._display;
    const requestData = { ...raw };
    delete requestData._display;
    try {
      const response = await callApi("/epr_settings/add", "POST", requestData);
      if (!response.ok) {
        return rejectWithValue("Server error");
      }
      const body = await response.json().catch(() => ({}));
      return typeof body === "object" && body !== null ? { ...body, _display } : { _display };
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchEPRCategories = createAsyncThunk(
  "epr/fetchEPRCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/subscription/epr_categories", "GET");
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const fetchEPROperators = createAsyncThunk(
  "epr/fetchEPROperators",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/epr_settings/epr_operators", "GET");
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue("Server error");
    }
  }
);

export const sendEprPaymentMemo = createAsyncThunk(
  "epr/sendEprPaymentMemo",
  async ({ id, file }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/manager/epr_reports/add_payment_memo?id=${id}`,
        "POST",
        file,
        {
          contentType: "multipart/form-data",
        }
      );

      if (!response.ok) {
        throw new Error(response.status);
      }

      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/** GET /transactions/filter — only send period_* when month filter applied; transaction_id when entered. */
const buildTransactionsFilterQuery = (rawQuery) => {
  const query = rawQuery && typeof rawQuery === "object" ? rawQuery : {};
  const filtered = {
    limit: query.limit ?? 25,
    offset: query.offset ?? 0,
  };

  const tid =
    query.transaction_id != null && String(query.transaction_id).trim() !== ""
      ? String(query.transaction_id).trim()
      : "";
  if (tid) {
    filtered.transaction_id = tid;
  }

  const byMonth =
    query.transactionsListMonthFilter === true &&
    query.period != null &&
    query.period !== "" &&
    query.period_year != null &&
    query.period_year !== "";

  if (byMonth) {
    filtered.period_type = query.period_type || "month";
    filtered.period_year = query.period_year;
    filtered.period = query.period;
  }

  return filtered;
};

export const getDeclarationTransaction = createAsyncThunk(
  "epr/getDeclarationTransaction",
  async (data, { rejectWithValue }) => {
    try {
      const query = data && typeof data === "object" ? { ...data } : {};
      const useFilterEndpoint = Boolean(query.useFilterEndpoint);
      delete query.useFilterEndpoint;
      const endpoint = useFilterEndpoint
        ? "/transactions/filter"
        : "/returns/declarations/transaction";
      const response = await callApi(
        endpoint,
        "GET",
        useFilterEndpoint ? buildTransactionsFilterQuery(query) : query
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/** Query params are passed as GET payload (callApi serializes to query string). Backend may expect e.g. sales_tax_return_id, line_key, limit, offset, transaction_id, from_date, to_date. */
export const getSalesTaxReturnTransactions = createAsyncThunk(
  "salesTax/getSalesTaxReturnTransactions",
  async (query, { rejectWithValue }) => {
    try {
      const requestQuery =
        query && typeof query === "object" ? { ...query } : {};
      const useFilterEndpoint = Boolean(requestQuery.useFilterEndpoint);
      delete requestQuery.useFilterEndpoint;
      const endpoint = useFilterEndpoint
        ? "/transactions/filter"
        : "/returns/sales_tax_returns/transaction";
      const response = await callApi(
        endpoint,
        "GET",
        useFilterEndpoint
          ? buildTransactionsFilterQuery(requestQuery)
          : requestQuery
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createSalesTaxReturnTransaction = createAsyncThunk(
  "salesTax/createSalesTaxReturnTransaction",
  async (body, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/returns/sales_tax_returns/transaction",
        "POST",
        body
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(handleErrorForThunk(err));
    }
  }
);

/** Remove / void a sales tax return transaction row (DELETE). */
export const voidSalesTaxReturnTransaction = createAsyncThunk(
  "salesTax/voidSalesTaxReturnTransaction",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/returns/sales_tax_returns/transaction/${id}`,
        "DELETE"
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
      if (response.status === 204) return { status: 204 };
      return response.json();
    } catch (err) {
      return rejectWithValue(handleErrorForThunk(err));
    }
  }
);

export const recalculateSalesTaxReturnTransactions = createAsyncThunk(
  "salesTax/recalculateSalesTaxReturnTransactions",
  async (query, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/returns/sales_tax_returns/transaction/recalculation",
        "GET",
        query
      );
      if (!response.ok) {
        throw new Error(response.status);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/** Sales tax: best-effort recalculation GET then transaction list (VAT uses list GET only). */
export const refreshSalesTaxReturnTransactionList = createAsyncThunk(
  "salesTax/refreshSalesTaxReturnTransactionList",
  async (query, { dispatch }) => {
    if (query?.useFilterEndpoint) {
      return dispatch(getSalesTaxReturnTransactions(query)).unwrap();
    }
    try {
      await dispatch(recalculateSalesTaxReturnTransactions(query)).unwrap();
    } catch {
      // ignore — list fetch still runs
    }
    return dispatch(getSalesTaxReturnTransactions(query)).unwrap();
  }
);

/** Input VAT import: GET /returns/transactions */
export const getReturnsTransactions = createAsyncThunk(
  "returns/getReturnsTransactions",
  async (query, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/returns/transactions",
        "GET",
        query && typeof query === "object" ? query : {}
      );
      if (!response.ok) {
        let detail = String(response.status);
        try {
          const errBody = await response.json();
          if (errBody?.detail || errBody?.error || errBody?.message) {
            detail =
              errBody.detail ||
              errBody.error ||
              errBody.message ||
              detail;
          }
        } catch {
          // keep status
        }
        return rejectWithValue(detail);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/** Input VAT import: POST /returns/transactions */
export const createReturnsTransaction = createAsyncThunk(
  "returns/createReturnsTransaction",
  async (body, { rejectWithValue }) => {
    try {
      const response = await callApi("/returns/transactions", "POST", body);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(handleErrorForThunk(err));
    }
  }
);

/** Input VAT import: PUT /returns/transactions/{transaction_id} */
export const editReturnsTransaction = createAsyncThunk(
  "returns/editReturnsTransaction",
  async ({ id, data: patch }, { rejectWithValue }) => {
    try {
      const body =
        patch && typeof patch === "object" && !Array.isArray(patch)
          ? { ...patch }
          : {};
      const response = await callApi(
        `/returns/transactions/${id}`,
        "PUT",
        body
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(handleErrorForThunk(err));
    }
  }
);

/** Normalize list payload from /returns/transactions or /recalculation */
const normalizeReturnsTransactionsPayload = (p) => {
  const items = Array.isArray(p) ? p : p?.items ?? p?.results ?? p?.transactions ?? [];
  const count =
    typeof p?.count === "number"
      ? p.count
      : typeof p?.total === "number"
        ? p.total
        : items.length;
  return {
    ...(p && typeof p === "object" && !Array.isArray(p) ? p : {}),
    items,
    count,
  };
};

/** Input VAT import: GET /returns/transactions/recalculation (list source for import modal) */
export const recalculateReturnsTransactions = createAsyncThunk(
  "returns/recalculateReturnsTransactions",
  async (query, { rejectWithValue }) => {
    try {
      const response = await callApi(
        "/returns/transactions/recalculation",
        "GET",
        query && typeof query === "object" ? query : {}
      );
      if (!response.ok) {
        let detail = String(response.status);
        try {
          const errBody = await response.json();
          if (errBody?.detail || errBody?.error || errBody?.message) {
            detail =
              errBody.detail ||
              errBody.error ||
              errBody.message ||
              detail;
          }
        } catch {
          // keep status
        }
        return rejectWithValue(detail);
      }
      return response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/** Input VAT import modal: reload list via GET /returns/transactions (recalculation runs on modal close). */
export const refreshReturnsTransactionList = createAsyncThunk(
  "returns/refreshReturnsTransactionList",
  async (query, { dispatch }) => {
    return dispatch(getReturnsTransactions(query)).unwrap();
  }
);

const declarationsSlice = createSlice({
  name: "declarations",
  initialState,
  reducers: {
    clearSubmittingEprReportError: (state) => {
      state.submittingEprReportError = null;
    },
    clearCreateReturnsTransactionError: (state) => {
      state.createReturnsTransactionError = null;
    },
    clearCreateSalesTaxReturnTransactionError: (state) => {
      state.createSalesTaxReturnTransactionError = null;
    },
    setDataForDac7Report: (state, action) => {
      state.dataForDac7ReportInfo = action.payload;
      const reportId = action.payload?.id ?? action.payload?.report_id;
      if (reportId != null) {
        state.dac7ReportId = reportId;
      }
    },
    setDac7ReportId: (state, action) => {
      state.dac7ReportId = action.payload;
    },
    selectCategory1: (state, action) => {
      const { value, checked } = action.payload;
      if (checked) {
        state.eprSubcategories.selectedCategory1.push(value);
      } else {
        state.eprSubcategories.selectedCategory1 =
          state.eprSubcategories.selectedCategory1.filter(
            (item) => item !== value
          );
      }
    },
    selectCategory2: (state, action) => {
      const { checked, category, subCategory } = action.payload;

      if (checked) {
        state.eprSubcategories.selectedCategory2.push([category, subCategory]);
      } else {
        const predicateCb = (item) => item[1] !== subCategory;

        state.eprSubcategories.selectedCategory2 =
          state.eprSubcategories.selectedCategory2.filter(predicateCb);
      }
    },
    
    selectCategory3: (state, action) => {
      const { checked, category, subCategory, subSubCategory } = action.payload;
      
      const newItem = [category, subCategory, subSubCategory];
      
      if (checked) {
        const exists = state.eprSubcategories.selectedCategory3.some(item =>
          item[0] === category &&
          item[1] === subCategory &&
          isEqual(item[2], subSubCategory)
        );
        
        if (!exists) {
          state.eprSubcategories.selectedCategory3.push(newItem);
        }
      } else {
        state.eprSubcategories.selectedCategory3 = state.eprSubcategories.selectedCategory3.filter(item =>
          !(item[0] === category && item[1] === subCategory && isEqual(item[2], subSubCategory))
        );
      }
    },
    
    selectCategory4: (state, action) => {
      const {
        checked,
        category,
        subCategory,
        subSubCategory,
        subSubSubCategory,
      } = action.payload;
      
      const newItem = [category, subCategory, subSubCategory, subSubSubCategory];
      
      if (checked) {
        const exists = state.eprSubcategories.selectedCategory4.some(item =>
          item[0] === category &&
          item[1] === subCategory &&
          isEqual(item[2], subSubCategory) &&
          isEqual(item[3], subSubSubCategory)
        );
        
        if (!exists) {
          state.eprSubcategories.selectedCategory4.push(newItem);
        }
      } else {
        state.eprSubcategories.selectedCategory4 = state.eprSubcategories.selectedCategory4.filter(item =>
          !(item[0] === category &&
            item[1] === subCategory &&
            isEqual(item[2], subSubCategory) &&
            isEqual(item[3], subSubSubCategory))
        );
      }
    },
    
    selectCategory5: (state, action) => {
      const {
        checked,
        category,
        subCategory,
        subSubCategory,
        subSubSubCategory,
        subSubSubSubCategory,
      } = action.payload;
   
      const newItem = [category, subCategory, subSubCategory, subSubSubCategory, subSubSubSubCategory];
      
      if (checked) {
        const exists = state.eprSubcategories.selectedCategory5.some(item =>
          item[0] === category &&
          item[1] === subCategory &&
          isEqual(item[2], subSubCategory) &&
          isEqual(item[3], subSubSubCategory) &&
          item[4] === subSubSubSubCategory
        );
        
        if (!exists) {
          state.eprSubcategories.selectedCategory5.push(newItem);
        }
      } else {
        state.eprSubcategories.selectedCategory5 = state.eprSubcategories.selectedCategory5.filter(item =>
          !(item[0] === category &&
            item[1] === subCategory &&
            isEqual(item[2], subSubCategory) &&
            isEqual(item[3], subSubSubCategory) &&
            item[4] === subSubSubSubCategory)
        );
      }
    },
    
    // Independent actions for Batteries 8-steps flow (NLD only)
    selectCategory6: (state, action) => {
      const {
        checked,
        category,
        subCategory,
        subSubCategory,
        subSubSubCategory,
        subSubSubSubCategory,
        subSubSubSubSubCategory,
      } = action.payload;
   
      const newItem = [category, subCategory, subSubCategory, subSubSubCategory, subSubSubSubCategory, subSubSubSubSubCategory];
      
      if (checked) {
        const exists = state.eprSubcategories.selectedCategory6.some(item =>
          item[0] === category &&
          item[1] === subCategory &&
          isEqual(item[2], subSubCategory) &&
          isEqual(item[3], subSubSubCategory) &&
          isEqual(item[4], subSubSubSubCategory) &&
          item[5] === subSubSubSubSubCategory
        );
        
        if (!exists) {
          state.eprSubcategories.selectedCategory6.push(newItem);
        }
      } else {
        state.eprSubcategories.selectedCategory6 = state.eprSubcategories.selectedCategory6.filter(item =>
          !(item[0] === category &&
            item[1] === subCategory &&
            isEqual(item[2], subSubCategory) &&
            isEqual(item[3], subSubSubCategory) &&
            isEqual(item[4], subSubSubSubCategory) &&
            item[5] === subSubSubSubSubCategory)
        );
      }
    },
    
    selectCategory7: (state, action) => {
      const {
        checked,
        category,
        subCategory,
        subSubCategory,
        subSubSubCategory,
        subSubSubSubCategory,
        subSubSubSubSubCategory,
        subSubSubSubSubSubCategory,
      } = action.payload;
   
      const newItem = [category, subCategory, subSubCategory, subSubSubCategory, subSubSubSubCategory, subSubSubSubSubCategory, subSubSubSubSubSubCategory];
      
      if (checked) {
        const exists = state.eprSubcategories.selectedCategory7.some(item =>
          item[0] === category &&
          item[1] === subCategory &&
          isEqual(item[2], subSubCategory) &&
          isEqual(item[3], subSubSubCategory) &&
          isEqual(item[4], subSubSubSubCategory) &&
          isEqual(item[5], subSubSubSubSubCategory) &&
          item[6] === subSubSubSubSubSubCategory
        );
        
        if (!exists) {
          state.eprSubcategories.selectedCategory7.push(newItem);
        }
      } else {
        state.eprSubcategories.selectedCategory7 = state.eprSubcategories.selectedCategory7.filter(item =>
          !(item[0] === category &&
            item[1] === subCategory &&
            isEqual(item[2], subSubCategory) &&
            isEqual(item[3], subSubSubCategory) &&
            isEqual(item[4], subSubSubSubCategory) &&
            isEqual(item[5], subSubSubSubSubCategory) &&
            item[6] === subSubSubSubSubSubCategory)
        );
      }
    },
    
    selectCategory8: (state, action) => {
      const {
        checked,
        category,
        subCategory,
        subSubCategory,
        subSubSubCategory,
        subSubSubSubCategory,
        subSubSubSubSubCategory,
        subSubSubSubSubSubCategory,
        subSubSubSubSubSubSubCategory,
      } = action.payload;
   
      const newItem = [category, subCategory, subSubCategory, subSubSubCategory, subSubSubSubCategory, subSubSubSubSubCategory, subSubSubSubSubSubCategory, subSubSubSubSubSubSubCategory];
      
      if (checked) {
        const exists = state.eprSubcategories.selectedCategory8.some(item =>
          item[0] === category &&
          item[1] === subCategory &&
          isEqual(item[2], subSubCategory) &&
          isEqual(item[3], subSubSubCategory) &&
          isEqual(item[4], subSubSubSubCategory) &&
          isEqual(item[5], subSubSubSubSubCategory) &&
          isEqual(item[6], subSubSubSubSubSubCategory) &&
          item[7] === subSubSubSubSubSubSubCategory
        );
        
        if (!exists) {
          state.eprSubcategories.selectedCategory8.push(newItem);
        }
      } else {
        state.eprSubcategories.selectedCategory8 = state.eprSubcategories.selectedCategory8.filter(item =>
          !(item[0] === category &&
            item[1] === subCategory &&
            isEqual(item[2], subSubCategory) &&
            isEqual(item[3], subSubSubCategory) &&
            isEqual(item[4], subSubSubSubCategory) &&
            isEqual(item[5], subSubSubSubSubCategory) &&
            isEqual(item[6], subSubSubSubSubSubCategory) &&
            item[7] === subSubSubSubSubSubSubCategory)
        );
      }
    },
    
    setQuantityArr: (state, action) => {
      if (Array.isArray(action.payload)) {
        		// If payload is an array of objects, simply replace the entire array
        state.eprSubcategories.quantityArr = action.payload;
      } else if (typeof action.payload === 'object' && action.payload !== null) {
        		// If payload is a single object, update or add by transaction_id
        const updatedObj = action.payload;
        const idx = state.eprSubcategories.quantityArr.findIndex(
          item => item.transaction_id === updatedObj.transaction_id
        );
        if (idx !== -1) {
          state.eprSubcategories.quantityArr[idx] = {
            ...state.eprSubcategories.quantityArr[idx],
            ...updatedObj
          };
        } else {
          state.eprSubcategories.quantityArr.push(updatedObj);
        }
      }
    },
    
    setChangeVariantCreatedDeclarationEpr: (state, action) => {
     state.changeVariantCreatedDeclarationEpr = action.payload
    },
    
    setQuantity: (state, action) => {
      const payload = action.payload;
      const quantity = payload.quantity;
      const ROW_KEYS = [
        'category', 'subCategory', 'subSubCategory', 'subSubSubCategory', 'recyclateOfPrimary',
        'subSubSubSubCategory', 'subSubSubSubSubCategory', 'subSubSubSubSubSubCategory',
        'subSubSubSubSubSubSubCategory', 'subSubSubSubSubSubSubSubCategory'
      ];
      const toVal = (v) => (v == null || v === '' ? null : (typeof v === 'object' && v?.name != null ? v.name : v));
      const newObj = { id: state.eprSubcategories.quantityArr.length + 1, quantity };
      ROW_KEYS.forEach((key) => {
        if (payload[key] !== undefined) newObj[key] = payload[key];
      });
      if (payload.units !== undefined) newObj.units = payload.units;
      // Match by all levels present in payload (supports 5–10 levels; normalise object vs string)
      const foundIndex = state.eprSubcategories.quantityArr.findIndex((item) => {
        for (let k = 0; k < ROW_KEYS.length; k++) {
          const key = ROW_KEYS[k];
          if (payload[key] === undefined) continue;
          const pVal = toVal(payload[key]);
          const iVal = toVal(item[key]);
          if (pVal !== iVal) return false;
        }
        return true;
      });
      if (foundIndex !== -1) {
        state.eprSubcategories.quantityArr[foundIndex].quantity = quantity;
        if (payload.units !== undefined) state.eprSubcategories.quantityArr[foundIndex].units = payload.units;
      } else {
        state.eprSubcategories.quantityArr.push(newObj);
      }
    },
    
    setQuantityForTwoSteps(state, action) {
      const { quantity, category, subCategory, units } = action.payload;
      if (!Array.isArray(state.eprSubcategories.quantityArr)) {
        state.eprSubcategories.quantityArr = [];
      }
      const newObj = {
        id: state.eprSubcategories.quantityArr.length + 1,
        quantity: quantity,
        category: category,
        subCategory: subCategory,
        units: units || state.eprSubcategories.unitsDefault || '',
      };
      
      const quantityArr = state.eprSubcategories.quantityArr ?? []
      const selectedCategory5 = state.eprSubcategories.selectedCategory5 ?? []
      const foundIndexEEE = quantityArr.findIndex(item =>
        item.category === category &&
        (!subCategory || item.subCategory === subCategory)
      );
      
      const foundIndexAll = selectedCategory5.findIndex(item =>
        item[0] === category &&
        item[1] === subCategory
      );
      
      const trueFoundIndex = foundIndexEEE
      if (trueFoundIndex !== -1) {
          state.eprSubcategories.quantityArr[trueFoundIndex].quantity = quantity
          if (units || state.eprSubcategories.unitsDefault) {
            state.eprSubcategories.quantityArr[trueFoundIndex].units = units || state.eprSubcategories.unitsDefault
          }
      } else {
        state.eprSubcategories.quantityArr.push(newObj)
      }
    },

    /**
     * New architecture: update one "amount" cell when is_many_units is true.
     * payload: { rowData: array (category path), amountIndex: number, value: string }
     * quantityArr item may have amount_0, amount_1, ... for many_units columns.
     */
    setQuantityManyUnitsAmount(state, action) {
      const { rowData, amountIndex, value } = action.payload;
      if (!Array.isArray(rowData) || amountIndex == null || !Array.isArray(state.eprSubcategories.quantityArr)) return;
      const ROW_KEYS = [
        'category', 'subCategory', 'subSubCategory', 'subSubSubCategory', 'recyclateOfPrimary',
        'subSubSubSubCategory', 'subSubSubSubSubCategory', 'subSubSubSubSubSubCategory',
        'subSubSubSubSubSubSubCategory', 'subSubSubSubSubSubSubSubCategory'
      ];
      const match = (item, arr) => {
        for (let k = 0; k < arr.length; k++) {
          const key = ROW_KEYS[k];
          if (!key) return false;
          const v = arr[k];
          const itemVal = item[key];
          if (v === undefined || v === null) {
            if (itemVal != null && itemVal !== '') return false;
          } else if (typeof v === 'object' && v?.name != null) {
            if (itemVal?.name !== v.name || (itemVal?.units ?? '') !== (v.units ?? '')) return false;
          } else if (itemVal !== v) return false;
        }
        for (let k = arr.length; k < ROW_KEYS.length; k++) {
          if (item[ROW_KEYS[k]] != null && item[ROW_KEYS[k]] !== '') return false;
        }
        return true;
      };
      const idx = state.eprSubcategories.quantityArr.findIndex((item) => match(item, rowData));
      const key = `amount_${amountIndex}`;
      if (idx !== -1) {
        state.eprSubcategories.quantityArr[idx][key] = value;
      } else {
        const newItem = { id: state.eprSubcategories.quantityArr.length + 1 };
        rowData.forEach((val, i) => {
          if (ROW_KEYS[i]) newItem[ROW_KEYS[i]] = val;
        });
        newItem[key] = value;
        state.eprSubcategories.quantityArr.push(newItem);
      }
    },
    
    setQuantityFor7StepsNLD(state, action) {
      const { 
        quantity, 
        amount,
        amount1_value,
        category, 
        subCategory, 
        subSubCategory, 
        subSubSubCategory,
        subSubSubSubCategory,
        subSubSubSubSubCategory,
        subSubSubSubSubSubCategory
      } = action.payload;
      
      const newObj = {
        id: state.eprSubcategories.quantityArr.length + 1,
        quantity: quantity,
        category: category,
        subCategory: subCategory,
        subSubCategory: subSubCategory,
        subSubSubCategory: subSubSubCategory,
        subSubSubSubCategory: subSubSubSubCategory,
        subSubSubSubSubCategory: subSubSubSubSubCategory,
        subSubSubSubSubSubCategory: subSubSubSubSubSubCategory,
      };

      // Add amount and amount1_value if provided
      if (amount !== undefined) {
        newObj.amount = amount;
      }
      if (amount1_value !== undefined) {
        newObj.amount1_value = amount1_value;
      }
      
      const foundIndex = state.eprSubcategories.quantityArr.findIndex(item => {
        const categoryMatch = item.category === category;
        const subCategoryMatch = item.subCategory === subCategory;
        const subSubCategoryMatch = subSubCategory?.name 
          ? (subSubCategory?.name === item.subSubCategory?.name && subSubCategory?.units === item.subSubCategory?.units)
          : (subSubCategory === item.subSubCategory || (subSubCategory === null && item.subSubCategory === null));
        const subSubSubCategoryMatch = subSubSubCategory?.name
          ? (subSubSubCategory?.name === item.subSubSubCategory?.name && subSubSubCategory?.units === item.subSubSubCategory?.units)
          : (subSubSubCategory === item.subSubSubCategory || (subSubSubCategory === null && item.subSubSubCategory === null));
        const subSubSubSubCategoryMatch = (item.subSubSubSubCategory === subSubSubSubCategory) || 
                                         (subSubSubSubCategory === null && item.subSubSubSubCategory === null);
        const subSubSubSubSubCategoryMatch = (item.subSubSubSubSubCategory === subSubSubSubSubCategory) || 
                                            (subSubSubSubSubCategory === null && item.subSubSubSubSubCategory === null);
        const subSubSubSubSubSubCategoryMatch = (item.subSubSubSubSubSubCategory === subSubSubSubSubSubCategory) || 
                                               (subSubSubSubSubSubCategory === null && item.subSubSubSubSubSubCategory === null);
        
        return categoryMatch && subCategoryMatch && subSubCategoryMatch && 
               subSubSubCategoryMatch && subSubSubSubCategoryMatch && 
               subSubSubSubSubCategoryMatch && subSubSubSubSubSubCategoryMatch;
      });
      
      if (foundIndex !== -1) {
        if (quantity !== undefined) {
        state.eprSubcategories.quantityArr[foundIndex].quantity = quantity;
        }
        if (amount !== undefined) {
          state.eprSubcategories.quantityArr[foundIndex].amount = amount;
        }
        if (amount1_value !== undefined) {
          state.eprSubcategories.quantityArr[foundIndex].amount1_value = amount1_value;
        }
      } else {
        state.eprSubcategories.quantityArr.push(newObj);
      }
    },
    
    // New isolated approach for NLD Batteries with objects and unique IDs
    createNldBatteriesItems: (state, action) => {
      const { selectedCategory3 } = action.payload;
      
      // Create objects from selectedCategory3
      state.eprSubcategories.nldBatteriesItems = selectedCategory3.map((item, index) => ({
        id: `nld-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
        subcategory_1: item[0],
        subcategory_2: item[1],
        subcategory_3: item[2]?.name || item[2],
        subcategory_4: null,
        subcategory_5: null,
        subcategory_6: null,
        subcategory_7: null,
        amount: '',
        quantity: '',
      }));
    },
    
    updateNldBatteriesSubcategory4: (state, action) => {
      const { itemIds, subcategory_4, checked, category, subCategory, subSubCategory } = action.payload;
      
      if (checked) {
        // When checking: create a new object for each base item if it doesn't already exist with this subcategory_4
        itemIds.forEach(itemId => {
          const baseItemIndex = state.eprSubcategories.nldBatteriesItems.findIndex(i => i.id === itemId);
          if (baseItemIndex !== -1) {
            const baseItem = state.eprSubcategories.nldBatteriesItems[baseItemIndex];
            // Use the base item's values or the provided values
            const itemCategory = category || baseItem.subcategory_1;
            const itemSubCategory = subCategory || baseItem.subcategory_2;
            const itemSubSubCategory = subSubCategory || baseItem.subcategory_3;
            
            // Check if an object with this exact combination already exists
            const existingItem = state.eprSubcategories.nldBatteriesItems.find(item => {
              const categoryMatch = item.subcategory_1 === itemCategory;
              const subCategoryMatch = item.subcategory_2 === itemSubCategory;
              const subSubCategoryMatch = 
                (typeof item.subcategory_3 === 'object' && typeof itemSubSubCategory === 'object' &&
                  item.subcategory_3?.name === itemSubSubCategory?.name && 
                  item.subcategory_3?.units === (itemSubSubCategory?.units || '')) ||
                (typeof item.subcategory_3 === 'string' && typeof itemSubSubCategory === 'string' && 
                  item.subcategory_3 === itemSubSubCategory) ||
                (item.subcategory_3 === itemSubSubCategory);
              const subcategory4Match = 
                (typeof item.subcategory_4 === 'object' && typeof subcategory_4 === 'object' &&
                  item.subcategory_4?.name === subcategory_4?.name && item.subcategory_4?.units === (subcategory_4?.units || '')) ||
                (typeof item.subcategory_4 === 'string' && typeof subcategory_4 === 'string' && item.subcategory_4 === subcategory_4) ||
                (item.subcategory_4 === subcategory_4);
              return categoryMatch && subCategoryMatch && subSubCategoryMatch && subcategory4Match;
            });
            
            // If no existing item with this subcategory_4, create a new one
            if (!existingItem) {
              const newItem = {
                id: `nld-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                subcategory_1: itemCategory,
                subcategory_2: itemSubCategory,
                subcategory_3: itemSubSubCategory,
                subcategory_4: subcategory_4,
                subcategory_5: null,
                subcategory_6: null,
                subcategory_7: null,
                amount: '',
                quantity: '',
              };
              
              // If base item has null subcategory_4, replace it to preserve order
              // Otherwise, find the last item with matching subcategory_1-3 and add after it to preserve order
              if (baseItem.subcategory_4 === null || baseItem.subcategory_4 === undefined || baseItem.subcategory_4 === '') {
                state.eprSubcategories.nldBatteriesItems[baseItemIndex] = newItem;
              } else {
                // Find the last item with matching subcategory_1-3 to insert after it
                let insertIndex = baseItemIndex;
                for (let i = baseItemIndex + 1; i < state.eprSubcategories.nldBatteriesItems.length; i++) {
                  const nextItem = state.eprSubcategories.nldBatteriesItems[i];
                  const categoryMatch = nextItem.subcategory_1 === itemCategory;
                  const subCategoryMatch = nextItem.subcategory_2 === itemSubCategory;
                  const subSubCategoryMatch = 
                    (typeof nextItem.subcategory_3 === 'object' && typeof itemSubSubCategory === 'object' &&
                      nextItem.subcategory_3?.name === itemSubSubCategory?.name && 
                      nextItem.subcategory_3?.units === (itemSubSubCategory?.units || '')) ||
                    (typeof nextItem.subcategory_3 === 'string' && typeof itemSubSubCategory === 'string' && 
                      nextItem.subcategory_3 === itemSubSubCategory) ||
                    (nextItem.subcategory_3 === itemSubSubCategory);
                  if (categoryMatch && subCategoryMatch && subSubCategoryMatch) {
                    insertIndex = i;
                  } else {
                    break;
                  }
                }
                // Insert after the last matching item to preserve order
                state.eprSubcategories.nldBatteriesItems.splice(insertIndex + 1, 0, newItem);
              }
            }
          }
        });
      } else {
        // When unchecking: replace objects with this subcategory_4 with base objects (null subcategory_4) to keep rows visible
        state.eprSubcategories.nldBatteriesItems = state.eprSubcategories.nldBatteriesItems.map(item => {
          const categoryMatch = item.subcategory_1 === category;
          const subCategoryMatch = item.subcategory_2 === subCategory;
          const subSubCategoryMatch = 
            (typeof item.subcategory_3 === 'object' && typeof subSubCategory === 'object' &&
              item.subcategory_3?.name === subSubCategory?.name && item.subcategory_3?.units === (subSubCategory?.units || '')) ||
            (typeof item.subcategory_3 === 'string' && typeof subSubCategory === 'string' && item.subcategory_3 === subSubCategory) ||
            (item.subcategory_3 === subSubCategory);
          const subcategory4Match = 
            (typeof item.subcategory_4 === 'object' && typeof subcategory_4 === 'object' &&
              item.subcategory_4?.name === subcategory_4?.name && item.subcategory_4?.units === (subcategory_4?.units || '')) ||
            (typeof item.subcategory_4 === 'string' && typeof subcategory_4 === 'string' && item.subcategory_4 === subcategory_4) ||
            (item.subcategory_4 === subcategory_4);
          
          // If matches, replace with base object (null subcategory_4) to keep row visible
          if (categoryMatch && subCategoryMatch && subSubCategoryMatch && subcategory4Match) {
            // Check if there are other objects with same subcategory_1-3 but different subcategory_4
            const hasOtherObjects = state.eprSubcategories.nldBatteriesItems.some(otherItem => {
              if (otherItem.id === item.id) return false;
              const otherCategoryMatch = otherItem.subcategory_1 === category;
              const otherSubCategoryMatch = otherItem.subcategory_2 === subCategory;
              const otherSubSubCategoryMatch = 
                (typeof otherItem.subcategory_3 === 'object' && typeof subSubCategory === 'object' &&
                  otherItem.subcategory_3?.name === subSubCategory?.name && otherItem.subcategory_3?.units === (subSubCategory?.units || '')) ||
                (typeof otherItem.subcategory_3 === 'string' && typeof subSubCategory === 'string' && otherItem.subcategory_3 === subSubCategory) ||
                (otherItem.subcategory_3 === subSubCategory);
              return otherCategoryMatch && otherSubCategoryMatch && otherSubSubCategoryMatch;
            });
            
            // If no other objects exist, replace with base object (null subcategory_4)
            // Otherwise, remove this object
            if (!hasOtherObjects) {
              return {
                ...item,
                subcategory_4: null,
                subcategory_5: null,
                subcategory_6: null,
                subcategory_7: null,
                amount: '',
                quantity: '',
              };
            } else {
              // Mark for removal by returning null
              return null;
            }
          }
          return item;
        }).filter(item => item !== null);
      }
    },
    
    updateNldBatteriesSubcategory5: (state, action) => {
      const { itemIds, subcategory_5, checked, category, subCategory, subSubCategory, subSubSubCategory } = action.payload;
      
      if (checked) {
        // When checking: create a new object for each base item if it doesn't already exist with this subcategory_5
        const itemsToRemove = [];
        itemIds.forEach(itemId => {
          const baseItem = state.eprSubcategories.nldBatteriesItems.find(i => i.id === itemId);
          if (baseItem) {
            const itemCategory = category || baseItem.subcategory_1;
            const itemSubCategory = subCategory || baseItem.subcategory_2;
            const itemSubSubCategory = subSubCategory || baseItem.subcategory_3;
            const itemSubSubSubCategory = subSubSubCategory || baseItem.subcategory_4;
            
            // Check if an object with this exact combination already exists
            const existingItem = state.eprSubcategories.nldBatteriesItems.find(item => {
              const categoryMatch = item.subcategory_1 === itemCategory;
              const subCategoryMatch = item.subcategory_2 === itemSubCategory;
              const subSubCategoryMatch = 
                (typeof item.subcategory_3 === 'object' && typeof itemSubSubCategory === 'object' &&
                  item.subcategory_3?.name === itemSubSubCategory?.name && 
                  item.subcategory_3?.units === (itemSubSubCategory?.units || '')) ||
                (typeof item.subcategory_3 === 'string' && typeof itemSubSubCategory === 'string' && 
                  item.subcategory_3 === itemSubSubCategory) ||
                (item.subcategory_3 === itemSubSubCategory);
              const subSubSubCategoryMatch = 
                (typeof item.subcategory_4 === 'object' && typeof itemSubSubSubCategory === 'object' &&
                  item.subcategory_4?.name === itemSubSubSubCategory?.name && 
                  item.subcategory_4?.units === (itemSubSubSubCategory?.units || '')) ||
                (typeof item.subcategory_4 === 'string' && typeof itemSubSubSubCategory === 'string' && 
                  item.subcategory_4 === itemSubSubSubCategory) ||
                (item.subcategory_4 === itemSubSubSubCategory);
              const subcategory5Match = item.subcategory_5 === subcategory_5;
              return categoryMatch && subCategoryMatch && subSubCategoryMatch && subSubSubCategoryMatch && subcategory5Match;
            });
            
            if (!existingItem) {
              const newItem = {
                id: `nld-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                subcategory_1: itemCategory,
                subcategory_2: itemSubCategory,
                subcategory_3: itemSubSubCategory,
                subcategory_4: itemSubSubSubCategory,
                subcategory_5: subcategory_5,
                subcategory_6: null,
                subcategory_7: null,
                amount: '',
                quantity: '',
              };
              
              // If base item has null subcategory_5, replace it to preserve order
              // Otherwise, find the last item with matching subcategory_1-4 and add after it to preserve order
              const baseItemIndex = state.eprSubcategories.nldBatteriesItems.findIndex(i => i.id === itemId);
              if (baseItem.subcategory_5 === null || baseItem.subcategory_5 === undefined || baseItem.subcategory_5 === '') {
                if (baseItemIndex !== -1) {
                  state.eprSubcategories.nldBatteriesItems[baseItemIndex] = newItem;
                } else {
                  state.eprSubcategories.nldBatteriesItems.push(newItem);
                }
              } else {
                // Find the last item with matching subcategory_1-4 to insert after it
                let insertIndex = baseItemIndex !== -1 ? baseItemIndex : state.eprSubcategories.nldBatteriesItems.length - 1;
                for (let i = insertIndex + 1; i < state.eprSubcategories.nldBatteriesItems.length; i++) {
                  const nextItem = state.eprSubcategories.nldBatteriesItems[i];
                  const categoryMatch = nextItem.subcategory_1 === itemCategory;
                  const subCategoryMatch = nextItem.subcategory_2 === itemSubCategory;
                  const subSubCategoryMatch = 
                    (typeof nextItem.subcategory_3 === 'object' && typeof itemSubSubCategory === 'object' &&
                      nextItem.subcategory_3?.name === itemSubSubCategory?.name && 
                      nextItem.subcategory_3?.units === (itemSubSubCategory?.units || '')) ||
                    (typeof nextItem.subcategory_3 === 'string' && typeof itemSubSubCategory === 'string' && 
                      nextItem.subcategory_3 === itemSubSubCategory) ||
                    (nextItem.subcategory_3 === itemSubSubCategory);
                  const subSubSubCategoryMatch = 
                    (typeof nextItem.subcategory_4 === 'object' && typeof itemSubSubSubCategory === 'object' &&
                      nextItem.subcategory_4?.name === itemSubSubSubCategory?.name && 
                      nextItem.subcategory_4?.units === (itemSubSubSubCategory?.units || '')) ||
                    (typeof nextItem.subcategory_4 === 'string' && typeof itemSubSubSubCategory === 'string' && 
                      nextItem.subcategory_4 === itemSubSubSubCategory) ||
                    (nextItem.subcategory_4 === itemSubSubSubCategory);
                  if (categoryMatch && subCategoryMatch && subSubCategoryMatch && subSubSubCategoryMatch) {
                    insertIndex = i;
                  } else {
                    break;
                  }
                }
                // Insert after the last matching item to preserve order
                state.eprSubcategories.nldBatteriesItems.splice(insertIndex + 1, 0, newItem);
              }
            }
          }
        });
      } else {
        // When unchecking: replace objects with this subcategory_5 with base objects (null subcategory_5) to keep rows visible
        state.eprSubcategories.nldBatteriesItems = state.eprSubcategories.nldBatteriesItems.map(item => {
          const categoryMatch = item.subcategory_1 === category;
          const subCategoryMatch = item.subcategory_2 === subCategory;
          const subSubCategoryMatch = 
            (typeof item.subcategory_3 === 'object' && typeof subSubCategory === 'object' &&
              item.subcategory_3?.name === subSubCategory?.name && item.subcategory_3?.units === (subSubCategory?.units || '')) ||
            (typeof item.subcategory_3 === 'string' && typeof subSubCategory === 'string' && item.subcategory_3 === subSubCategory) ||
            (item.subcategory_3 === subSubCategory);
          const subSubSubCategoryMatch = 
            (typeof item.subcategory_4 === 'object' && typeof subSubSubCategory === 'object' &&
              item.subcategory_4?.name === subSubSubCategory?.name && item.subcategory_4?.units === (subSubSubCategory?.units || '')) ||
            (typeof item.subcategory_4 === 'string' && typeof subSubSubCategory === 'string' && item.subcategory_4 === subSubSubCategory) ||
            (item.subcategory_4 === subSubSubCategory);
          const subcategory5Match = item.subcategory_5 === subcategory_5;
          
          if (categoryMatch && subCategoryMatch && subSubCategoryMatch && subSubSubCategoryMatch && subcategory5Match) {
            // Check if there are other objects with same subcategory_1-4 but different subcategory_5
            const hasOtherObjects = state.eprSubcategories.nldBatteriesItems.some(otherItem => {
              if (otherItem.id === item.id) return false;
              const otherCategoryMatch = otherItem.subcategory_1 === category;
              const otherSubCategoryMatch = otherItem.subcategory_2 === subCategory;
              const otherSubSubCategoryMatch = 
                (typeof otherItem.subcategory_3 === 'object' && typeof subSubCategory === 'object' &&
                  otherItem.subcategory_3?.name === subSubCategory?.name && otherItem.subcategory_3?.units === (subSubCategory?.units || '')) ||
                (typeof otherItem.subcategory_3 === 'string' && typeof subSubCategory === 'string' && otherItem.subcategory_3 === subSubCategory) ||
                (otherItem.subcategory_3 === subSubCategory);
              const otherSubSubSubCategoryMatch = 
                (typeof otherItem.subcategory_4 === 'object' && typeof subSubSubCategory === 'object' &&
                  otherItem.subcategory_4?.name === subSubSubCategory?.name && otherItem.subcategory_4?.units === (subSubSubCategory?.units || '')) ||
                (typeof otherItem.subcategory_4 === 'string' && typeof subSubSubCategory === 'string' && otherItem.subcategory_4 === subSubSubCategory) ||
                (otherItem.subcategory_4 === subSubSubCategory);
              return otherCategoryMatch && otherSubCategoryMatch && otherSubSubCategoryMatch && otherSubSubSubCategoryMatch;
            });
            
            if (!hasOtherObjects) {
              return {
                ...item,
                subcategory_5: null,
                subcategory_6: null,
                subcategory_7: null,
                amount: '',
                quantity: '',
              };
            } else {
              return null;
            }
          }
          return item;
        }).filter(item => item !== null);
      }
    },
    
    updateNldBatteriesSubcategory6: (state, action) => {
      const { itemIds, subcategory_6, checked, category, subCategory, subSubCategory, subSubSubCategory, subSubSubSubCategory } = action.payload;
      
      if (checked) {
        // If itemIds is empty, check if item exists and find insertion point
        if (itemIds.length === 0) {
          const itemCategory = category;
          const itemSubCategory = subCategory;
          const itemSubSubCategory = subSubCategory;
          const itemSubSubSubCategory = subSubSubCategory;
          const itemSubSubSubSubCategory = subSubSubSubCategory;
          
          // Check if an object with this exact combination already exists
          const existingItem = state.eprSubcategories.nldBatteriesItems.find(item => {
            const categoryMatch = item.subcategory_1 === itemCategory;
            const subCategoryMatch = item.subcategory_2 === itemSubCategory;
            const subSubCategoryMatch = 
              (typeof item.subcategory_3 === 'object' && typeof itemSubSubCategory === 'object' &&
                item.subcategory_3?.name === itemSubSubCategory?.name && 
                item.subcategory_3?.units === (itemSubSubCategory?.units || '')) ||
              (typeof item.subcategory_3 === 'string' && typeof itemSubSubCategory === 'string' && 
                item.subcategory_3 === itemSubSubCategory) ||
              (item.subcategory_3 === itemSubSubCategory);
            const subSubSubCategoryMatch = 
              (typeof item.subcategory_4 === 'object' && typeof itemSubSubSubCategory === 'object' &&
                item.subcategory_4?.name === itemSubSubSubCategory?.name && 
                item.subcategory_4?.units === (itemSubSubSubCategory?.units || '')) ||
              (typeof item.subcategory_4 === 'string' && typeof itemSubSubSubCategory === 'string' && 
                item.subcategory_4 === itemSubSubSubCategory) ||
              (item.subcategory_4 === itemSubSubSubCategory);
            const subSubSubSubCategoryMatch = item.subcategory_5 === itemSubSubSubSubCategory;
            const subcategory6Match = item.subcategory_6 === subcategory_6;
            return categoryMatch && subCategoryMatch && subSubCategoryMatch && subSubSubCategoryMatch && subSubSubSubCategoryMatch && subcategory6Match;
          });

          if (!existingItem) {
            // Find the last item with matching subcategory_1-5 to insert after it
            let insertIndex = -1;
            for (let i = state.eprSubcategories.nldBatteriesItems.length - 1; i >= 0; i--) {
              const item = state.eprSubcategories.nldBatteriesItems[i];
              const categoryMatch = item.subcategory_1 === itemCategory;
              const subCategoryMatch = item.subcategory_2 === itemSubCategory;
              const subSubCategoryMatch = 
                (typeof item.subcategory_3 === 'object' && typeof itemSubSubCategory === 'object' &&
                  item.subcategory_3?.name === itemSubSubCategory?.name && 
                  item.subcategory_3?.units === (itemSubSubCategory?.units || '')) ||
                (typeof item.subcategory_3 === 'string' && typeof itemSubSubCategory === 'string' && 
                  item.subcategory_3 === itemSubSubCategory) ||
                (item.subcategory_3 === itemSubSubCategory);
              const subSubSubCategoryMatch = 
                (typeof item.subcategory_4 === 'object' && typeof itemSubSubSubCategory === 'object' &&
                  item.subcategory_4?.name === itemSubSubSubCategory?.name && 
                  item.subcategory_4?.units === (itemSubSubSubCategory?.units || '')) ||
                (typeof item.subcategory_4 === 'string' && typeof itemSubSubSubCategory === 'string' && 
                  item.subcategory_4 === itemSubSubSubCategory) ||
                (item.subcategory_4 === itemSubSubSubCategory);
              const subSubSubSubCategoryMatch = item.subcategory_5 === itemSubSubSubSubCategory;
              
              if (categoryMatch && subCategoryMatch && subSubCategoryMatch && subSubSubCategoryMatch && subSubSubSubCategoryMatch) {
                insertIndex = i;
                break;
              }
            }
            
            const newItem = {
              id: `nld-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              subcategory_1: itemCategory,
              subcategory_2: itemSubCategory,
              subcategory_3: itemSubSubCategory,
              subcategory_4: itemSubSubSubCategory,
              subcategory_5: itemSubSubSubSubCategory,
              subcategory_6: subcategory_6,
              subcategory_7: null,
              amount: '',
              quantity: '',
            };
            
            if (insertIndex !== -1) {
              state.eprSubcategories.nldBatteriesItems.splice(insertIndex + 1, 0, newItem);
            } else {
              state.eprSubcategories.nldBatteriesItems.push(newItem);
            }
          }
          return;
        }
        
        // When checking: create a new object for each base item if it doesn't already exist with this subcategory_6
        itemIds.forEach(itemId => {
          const baseItem = state.eprSubcategories.nldBatteriesItems.find(i => i.id === itemId);
          if (baseItem) {
            const itemCategory = category || baseItem.subcategory_1;
            const itemSubCategory = subCategory || baseItem.subcategory_2;
            const itemSubSubCategory = subSubCategory || baseItem.subcategory_3;
            const itemSubSubSubCategory = subSubSubCategory || baseItem.subcategory_4;
            const itemSubSubSubSubCategory = subSubSubSubCategory || baseItem.subcategory_5;
            
            // Check if an object with this exact combination already exists
            const existingItem = state.eprSubcategories.nldBatteriesItems.find(item => {
              const categoryMatch = item.subcategory_1 === itemCategory;
              const subCategoryMatch = item.subcategory_2 === itemSubCategory;
              const subSubCategoryMatch = 
                (typeof item.subcategory_3 === 'object' && typeof itemSubSubCategory === 'object' &&
                  item.subcategory_3?.name === itemSubSubCategory?.name && 
                  item.subcategory_3?.units === (itemSubSubCategory?.units || '')) ||
                (typeof item.subcategory_3 === 'string' && typeof itemSubSubCategory === 'string' && 
                  item.subcategory_3 === itemSubSubCategory) ||
                (item.subcategory_3 === itemSubSubCategory);
              const subSubSubCategoryMatch = 
                (typeof item.subcategory_4 === 'object' && typeof itemSubSubSubCategory === 'object' &&
                  item.subcategory_4?.name === itemSubSubSubCategory?.name && 
                  item.subcategory_4?.units === (itemSubSubSubCategory?.units || '')) ||
                (typeof item.subcategory_4 === 'string' && typeof itemSubSubSubCategory === 'string' && 
                  item.subcategory_4 === itemSubSubSubCategory) ||
                (item.subcategory_4 === itemSubSubSubCategory);
              const subSubSubSubCategoryMatch = item.subcategory_5 === itemSubSubSubSubCategory;
              const subcategory6Match = item.subcategory_6 === subcategory_6;
              return categoryMatch && subCategoryMatch && subSubCategoryMatch && subSubSubCategoryMatch && subSubSubSubCategoryMatch && subcategory6Match;
            });

            if (!existingItem) {
              const newItem = {
                id: `nld-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                subcategory_1: itemCategory,
                subcategory_2: itemSubCategory,
                subcategory_3: itemSubSubCategory,
                subcategory_4: itemSubSubSubCategory,
                subcategory_5: itemSubSubSubSubCategory,
                subcategory_6: subcategory_6,
                subcategory_7: null,
                amount: '',
                quantity: '',
              };
              
              // If base item has null subcategory_6, replace it to preserve order
              // Otherwise, find the last item with matching subcategory_1-5 and add after it to preserve order
              const baseItemIndex = state.eprSubcategories.nldBatteriesItems.findIndex(i => i.id === itemId);
              if (baseItem.subcategory_6 === null || baseItem.subcategory_6 === undefined || baseItem.subcategory_6 === '') {
                if (baseItemIndex !== -1) {
                  state.eprSubcategories.nldBatteriesItems[baseItemIndex] = newItem;
                } else {
                  state.eprSubcategories.nldBatteriesItems.push(newItem);
                }
              } else {
                // Find the last item with matching subcategory_1-5 to insert after it
                let insertIndex = baseItemIndex !== -1 ? baseItemIndex : state.eprSubcategories.nldBatteriesItems.length - 1;
                for (let i = insertIndex + 1; i < state.eprSubcategories.nldBatteriesItems.length; i++) {
                  const nextItem = state.eprSubcategories.nldBatteriesItems[i];
                  const categoryMatch = nextItem.subcategory_1 === itemCategory;
                  const subCategoryMatch = nextItem.subcategory_2 === itemSubCategory;
                  const subSubCategoryMatch = 
                    (typeof nextItem.subcategory_3 === 'object' && typeof itemSubSubCategory === 'object' &&
                      nextItem.subcategory_3?.name === itemSubSubCategory?.name && 
                      nextItem.subcategory_3?.units === (itemSubSubCategory?.units || '')) ||
                    (typeof nextItem.subcategory_3 === 'string' && typeof itemSubSubCategory === 'string' && 
                      nextItem.subcategory_3 === itemSubSubCategory) ||
                    (nextItem.subcategory_3 === itemSubSubCategory);
                  const subSubSubCategoryMatch = 
                    (typeof nextItem.subcategory_4 === 'object' && typeof itemSubSubSubCategory === 'object' &&
                      nextItem.subcategory_4?.name === itemSubSubSubCategory?.name && 
                      nextItem.subcategory_4?.units === (itemSubSubSubCategory?.units || '')) ||
                    (typeof nextItem.subcategory_4 === 'string' && typeof itemSubSubSubCategory === 'string' && 
                      nextItem.subcategory_4 === itemSubSubSubCategory) ||
                    (nextItem.subcategory_4 === itemSubSubSubCategory);
                  const subSubSubSubCategoryMatch = nextItem.subcategory_5 === itemSubSubSubSubCategory;
                  if (categoryMatch && subCategoryMatch && subSubCategoryMatch && subSubSubCategoryMatch && subSubSubSubCategoryMatch) {
                    insertIndex = i;
                  } else {
                    break;
                  }
                }
                // Insert after the last matching item to preserve order
                state.eprSubcategories.nldBatteriesItems.splice(insertIndex + 1, 0, newItem);
              }
            }
          }
        });
      } else {
        // When unchecking: replace objects with this subcategory_6 with base objects (null subcategory_6) to keep rows visible
        // Same logic as step 5 and step 7
        state.eprSubcategories.nldBatteriesItems = state.eprSubcategories.nldBatteriesItems.map(item => {
          const categoryMatch = item.subcategory_1 === category;
          const subCategoryMatch = item.subcategory_2 === subCategory;
          const subSubCategoryMatch = 
            (typeof item.subcategory_3 === 'object' && typeof subSubCategory === 'object' &&
              item.subcategory_3?.name === subSubCategory?.name && item.subcategory_3?.units === (subSubCategory?.units || '')) ||
            (typeof item.subcategory_3 === 'string' && typeof subSubCategory === 'string' && item.subcategory_3 === subSubCategory) ||
            (item.subcategory_3 === subSubCategory);
          const subSubSubCategoryMatch = 
            (typeof item.subcategory_4 === 'object' && typeof subSubSubCategory === 'object' &&
              item.subcategory_4?.name === subSubSubCategory?.name && item.subcategory_4?.units === (subSubSubCategory?.units || '')) ||
            (typeof item.subcategory_4 === 'string' && typeof subSubSubCategory === 'string' && item.subcategory_4 === subSubSubCategory) ||
            (item.subcategory_4 === subSubSubCategory);
          const subSubSubSubCategoryMatch = item.subcategory_5 === subSubSubSubCategory;
          const subcategory6Match = item.subcategory_6 === subcategory_6;
          
          if (categoryMatch && subCategoryMatch && subSubCategoryMatch && subSubSubCategoryMatch && subSubSubSubCategoryMatch && subcategory6Match) {
            // Check if there are other objects with same subcategory_1-5 but different subcategory_6
            const hasOtherObjects = state.eprSubcategories.nldBatteriesItems.some(otherItem => {
              if (otherItem.id === item.id) return false;
              const otherCategoryMatch = otherItem.subcategory_1 === category;
              const otherSubCategoryMatch = otherItem.subcategory_2 === subCategory;
              const otherSubSubCategoryMatch = 
                (typeof otherItem.subcategory_3 === 'object' && typeof subSubCategory === 'object' &&
                  otherItem.subcategory_3?.name === subSubCategory?.name && otherItem.subcategory_3?.units === (subSubCategory?.units || '')) ||
                (typeof otherItem.subcategory_3 === 'string' && typeof subSubCategory === 'string' && otherItem.subcategory_3 === subSubCategory) ||
                (otherItem.subcategory_3 === subSubCategory);
              const otherSubSubSubCategoryMatch = 
                (typeof otherItem.subcategory_4 === 'object' && typeof subSubSubCategory === 'object' &&
                  otherItem.subcategory_4?.name === subSubSubCategory?.name && otherItem.subcategory_4?.units === (subSubSubCategory?.units || '')) ||
                (typeof otherItem.subcategory_4 === 'string' && typeof subSubSubCategory === 'string' && otherItem.subcategory_4 === subSubSubCategory) ||
                (otherItem.subcategory_4 === subSubSubCategory);
              const otherSubSubSubSubCategoryMatch = otherItem.subcategory_5 === subSubSubSubCategory;
              return otherCategoryMatch && otherSubCategoryMatch && otherSubSubCategoryMatch && otherSubSubSubCategoryMatch && otherSubSubSubSubCategoryMatch;
            });
            
            if (!hasOtherObjects) {
              return {
                ...item,
                subcategory_6: null,
                subcategory_7: null,
                amount: '',
                quantity: '',
              };
            } else {
              return null;
            }
          }
          return item;
        }).filter(item => item !== null);
      }
    },
    
    updateNldBatteriesSubcategory7: (state, action) => {
      const { itemIds, subcategory_7, checked, category, subCategory, subSubCategory, subSubSubCategory, subSubSubSubCategory, subSubSubSubSubCategory } = action.payload;
      
      if (checked) {
        // When checking: create a new object for each base item if it doesn't already exist with this subcategory_7
        // Same logic as step 5 and step 6
        itemIds.forEach(itemId => {
          const baseItem = state.eprSubcategories.nldBatteriesItems.find(i => i.id === itemId);
          if (baseItem) {
            const itemCategory = category || baseItem.subcategory_1;
            const itemSubCategory = subCategory || baseItem.subcategory_2;
            const itemSubSubCategory = subSubCategory || baseItem.subcategory_3;
            const itemSubSubSubCategory = subSubSubCategory || baseItem.subcategory_4;
            const itemSubSubSubSubCategory = subSubSubSubCategory || baseItem.subcategory_5;
            const itemSubSubSubSubSubCategory = subSubSubSubSubCategory || baseItem.subcategory_6;
            
            // Check if an object with this exact combination already exists
            const existingItem = state.eprSubcategories.nldBatteriesItems.find(item => {
              const categoryMatch = item.subcategory_1 === itemCategory;
              const subCategoryMatch = item.subcategory_2 === itemSubCategory;
              const subSubCategoryMatch = 
                (typeof item.subcategory_3 === 'object' && typeof itemSubSubCategory === 'object' &&
                  item.subcategory_3?.name === itemSubSubCategory?.name && 
                  item.subcategory_3?.units === (itemSubSubCategory?.units || '')) ||
                (typeof item.subcategory_3 === 'string' && typeof itemSubSubCategory === 'string' && 
                  item.subcategory_3 === itemSubSubCategory) ||
                (item.subcategory_3 === itemSubSubCategory);
              const subSubSubCategoryMatch = 
                (typeof item.subcategory_4 === 'object' && typeof itemSubSubSubCategory === 'object' &&
                  item.subcategory_4?.name === itemSubSubSubCategory?.name && 
                  item.subcategory_4?.units === (itemSubSubSubCategory?.units || '')) ||
                (typeof item.subcategory_4 === 'string' && typeof itemSubSubSubCategory === 'string' && 
                  item.subcategory_4 === itemSubSubSubCategory) ||
                (item.subcategory_4 === itemSubSubSubCategory);
              const subSubSubSubCategoryMatch = item.subcategory_5 === itemSubSubSubSubCategory;
              const subSubSubSubSubCategoryMatch = item.subcategory_6 === itemSubSubSubSubSubCategory;
              const subcategory7Match = item.subcategory_7 === subcategory_7;
              return categoryMatch && subCategoryMatch && subSubCategoryMatch && subSubSubCategoryMatch && subSubSubSubCategoryMatch && subSubSubSubSubCategoryMatch && subcategory7Match;
            });
            
            if (!existingItem) {
              const newItem = {
                id: `nld-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                subcategory_1: itemCategory,
                subcategory_2: itemSubCategory,
                subcategory_3: itemSubSubCategory,
                subcategory_4: itemSubSubSubCategory,
                subcategory_5: itemSubSubSubSubCategory,
                subcategory_6: itemSubSubSubSubSubCategory,
                subcategory_7: subcategory_7,
                amount: '',
                quantity: '',
              };
              
              // If base item has null subcategory_7, replace it to preserve order
              // Otherwise, find the last item with matching subcategory_1-6 and add after it to preserve order
              const baseItemIndex = state.eprSubcategories.nldBatteriesItems.findIndex(i => i.id === itemId);
              if (baseItem.subcategory_7 === null || baseItem.subcategory_7 === undefined || baseItem.subcategory_7 === '') {
                if (baseItemIndex !== -1) {
                  state.eprSubcategories.nldBatteriesItems[baseItemIndex] = newItem;
                } else {
                  state.eprSubcategories.nldBatteriesItems.push(newItem);
                }
              } else {
                // Find the last item with matching subcategory_1-6 to insert after it
                let insertIndex = baseItemIndex !== -1 ? baseItemIndex : state.eprSubcategories.nldBatteriesItems.length - 1;
                for (let i = insertIndex + 1; i < state.eprSubcategories.nldBatteriesItems.length; i++) {
                  const nextItem = state.eprSubcategories.nldBatteriesItems[i];
                  const categoryMatch = nextItem.subcategory_1 === itemCategory;
                  const subCategoryMatch = nextItem.subcategory_2 === itemSubCategory;
                  const subSubCategoryMatch = 
                    (typeof nextItem.subcategory_3 === 'object' && typeof itemSubSubCategory === 'object' &&
                      nextItem.subcategory_3?.name === itemSubSubCategory?.name && 
                      nextItem.subcategory_3?.units === (itemSubSubCategory?.units || '')) ||
                    (typeof nextItem.subcategory_3 === 'string' && typeof itemSubSubCategory === 'string' && 
                      nextItem.subcategory_3 === itemSubSubCategory) ||
                    (nextItem.subcategory_3 === itemSubSubCategory);
                  const subSubSubCategoryMatch = 
                    (typeof nextItem.subcategory_4 === 'object' && typeof itemSubSubSubCategory === 'object' &&
                      nextItem.subcategory_4?.name === itemSubSubSubCategory?.name && 
                      nextItem.subcategory_4?.units === (itemSubSubSubCategory?.units || '')) ||
                    (typeof nextItem.subcategory_4 === 'string' && typeof itemSubSubSubCategory === 'string' && 
                      nextItem.subcategory_4 === itemSubSubSubCategory) ||
                    (nextItem.subcategory_4 === itemSubSubSubCategory);
                  const subSubSubSubCategoryMatch = nextItem.subcategory_5 === itemSubSubSubSubCategory;
                  const subSubSubSubSubCategoryMatch = nextItem.subcategory_6 === itemSubSubSubSubSubCategory;
                  if (categoryMatch && subCategoryMatch && subSubCategoryMatch && subSubSubCategoryMatch && subSubSubSubCategoryMatch && subSubSubSubSubCategoryMatch) {
                    insertIndex = i;
                  } else {
                    break;
                  }
                }
                // Insert after the last matching item to preserve order
                state.eprSubcategories.nldBatteriesItems.splice(insertIndex + 1, 0, newItem);
              }
            }
          }
        });
      } else {
        // When unchecking: replace objects with this subcategory_7 with base objects (null subcategory_7) to keep rows visible
        state.eprSubcategories.nldBatteriesItems = state.eprSubcategories.nldBatteriesItems.map(item => {
          const categoryMatch = item.subcategory_1 === category;
          const subCategoryMatch = item.subcategory_2 === subCategory;
          const subSubCategoryMatch = 
            (typeof item.subcategory_3 === 'object' && typeof subSubCategory === 'object' &&
              item.subcategory_3?.name === subSubCategory?.name && item.subcategory_3?.units === (subSubCategory?.units || '')) ||
            (typeof item.subcategory_3 === 'string' && typeof subSubCategory === 'string' && item.subcategory_3 === subSubCategory) ||
            (item.subcategory_3 === subSubCategory);
          const subSubSubCategoryMatch = 
            (typeof item.subcategory_4 === 'object' && typeof subSubSubCategory === 'object' &&
              item.subcategory_4?.name === subSubSubCategory?.name && item.subcategory_4?.units === (subSubSubCategory?.units || '')) ||
            (typeof item.subcategory_4 === 'string' && typeof subSubSubCategory === 'string' && item.subcategory_4 === subSubSubCategory) ||
            (item.subcategory_4 === subSubSubCategory);
          const subSubSubSubCategoryMatch = item.subcategory_5 === subSubSubSubCategory;
          const subSubSubSubSubCategoryMatch = item.subcategory_6 === subSubSubSubSubCategory;
          const subcategory7Match = item.subcategory_7 === subcategory_7;
          
          if (categoryMatch && subCategoryMatch && subSubCategoryMatch && subSubSubCategoryMatch && subSubSubSubCategoryMatch && subSubSubSubSubCategoryMatch && subcategory7Match) {
            // Check if there are other objects with same subcategory_1-6 but different subcategory_7
            const hasOtherObjects = state.eprSubcategories.nldBatteriesItems.some(otherItem => {
              if (otherItem.id === item.id) return false;
              const otherCategoryMatch = otherItem.subcategory_1 === category;
              const otherSubCategoryMatch = otherItem.subcategory_2 === subCategory;
              const otherSubSubCategoryMatch = 
                (typeof otherItem.subcategory_3 === 'object' && typeof subSubCategory === 'object' &&
                  otherItem.subcategory_3?.name === subSubCategory?.name && otherItem.subcategory_3?.units === (subSubCategory?.units || '')) ||
                (typeof otherItem.subcategory_3 === 'string' && typeof subSubCategory === 'string' && otherItem.subcategory_3 === subSubCategory) ||
                (otherItem.subcategory_3 === subSubCategory);
              const otherSubSubSubCategoryMatch = 
                (typeof otherItem.subcategory_4 === 'object' && typeof subSubSubCategory === 'object' &&
                  otherItem.subcategory_4?.name === subSubSubCategory?.name && otherItem.subcategory_4?.units === (subSubSubCategory?.units || '')) ||
                (typeof otherItem.subcategory_4 === 'string' && typeof subSubSubCategory === 'string' && otherItem.subcategory_4 === subSubSubCategory) ||
                (otherItem.subcategory_4 === subSubSubCategory);
              const otherSubSubSubSubCategoryMatch = otherItem.subcategory_5 === subSubSubSubCategory;
              const otherSubSubSubSubSubCategoryMatch = otherItem.subcategory_6 === subSubSubSubSubCategory;
              return otherCategoryMatch && otherSubCategoryMatch && otherSubSubCategoryMatch && otherSubSubSubCategoryMatch && otherSubSubSubSubCategoryMatch && otherSubSubSubSubSubCategoryMatch;
            });
            
            if (!hasOtherObjects) {
              return {
                ...item,
                subcategory_7: null,
                amount: '',
                quantity: '',
              };
            } else {
              return null;
            }
          }
          return item;
        }).filter(item => item !== null);
      }
    },
    
    updateNldBatteriesQuantity: (state, action) => {
      const { itemId, amount, quantity } = action.payload;
      
      const item = state.eprSubcategories.nldBatteriesItems.find(i => i.id === itemId);
      if (item) {
        if (amount !== undefined) item.amount = amount;
        if (quantity !== undefined) item.quantity = quantity;
      }
    },
    
    resetNldBatteriesItems: (state) => {
      state.eprSubcategories.nldBatteriesItems = [];
    },
    
    setVatReturnsData: (state, action) => {
      state.dataVatReturn = action.payload;
    },
    setResetData: (state, action) => {
      state.eprSubcategories.quantityArr = action.payload.quantityArr
      if (action.payload.selectedCategory5 !== undefined) {
        state.eprSubcategories.selectedCategory5 = action.payload.selectedCategory5
      }
      if (action.payload.selectedCategory4 !== undefined) {
        state.eprSubcategories.selectedCategory4 = action.payload.selectedCategory4
      }
      state.eprSubcategories.selectedCategory3 = action.payload.selectedCategory3
      state.eprSubcategories.selectedCategory2 = action.payload.selectedCategory2
      state.eprSubcategories.selectedCategory1 = action.payload.selectedCategory1
      // Set unitsDefault if provided
      if (action.payload.unitsDefault !== undefined) {
        state.eprSubcategories.unitsDefault = action.payload.unitsDefault
      }
      // Reset new categories if provided (for Batteries 8-steps flow)
      if (action.payload.selectedCategory6 !== undefined) {
        state.eprSubcategories.selectedCategory6 = action.payload.selectedCategory6
      }
      if (action.payload.selectedCategory7 !== undefined) {
        state.eprSubcategories.selectedCategory7 = action.payload.selectedCategory7
      }
      if (action.payload.selectedCategory8 !== undefined) {
        state.eprSubcategories.selectedCategory8 = action.payload.selectedCategory8
      }
      // Reset NLD Batteries items if provided
      if (action.payload.nldBatteriesItems !== undefined) {
        state.eprSubcategories.nldBatteriesItems = action.payload.nldBatteriesItems
      }
    },
    setResetEprDeclaration: (state, action) => {
      state.eprDeclaration = action.payload
    },
    // Sync from new EPR architecture (EprDeclarationNewArchitecture) so Quantity/Summary can read selection
    setSelectedCategoriesFromNewArch: (state, action) => {
      const selectedByLevel = action.payload?.selectedByLevel ?? [];
      state.eprSubcategories.selectedCategory1 = (selectedByLevel[0] ?? []).map((row) => (Array.isArray(row) ? row[0] : row));
      state.eprSubcategories.selectedCategory2 = selectedByLevel[1] ?? [];
      state.eprSubcategories.selectedCategory3 = selectedByLevel[2] ?? [];
      state.eprSubcategories.selectedCategory4 = selectedByLevel[3] ?? [];
      state.eprSubcategories.selectedCategory5 = selectedByLevel[4] ?? [];
      state.eprSubcategories.selectedCategory6 = selectedByLevel[5] ?? [];
      state.eprSubcategories.selectedCategory7 = selectedByLevel[6] ?? [];
      state.eprSubcategories.selectedCategory8 = selectedByLevel[7] ?? [];
    },
    setDataCreateEprDeclaration: (state, action) => {
      state.dataCreateEprDeclaration = action.payload
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchDeclarations.pending, (state) => {
        state.fetching = true;
      })
      .addCase(fetchDeclarations.fulfilled, (state, action) => {
        state.fetching = false;
        state.fetchingError = null;
        state.declarations = { ...action.payload };
      })
      .addCase(fetchDeclarations.rejected, (state, action) => {
        state.fetching = false;
        state.fetchingError = parseServerError(action.error);
      })
      .addCase(downloadDeclarationFile.pending, (state) => {
        state.downloading = true;
      })
      .addCase(downloadDeclarationFile.fulfilled, (state, action) => {
        state.downloading = false;
        state.downloadingError = null;
        state.toDownload = { ...action.payload };
      })
      .addCase(downloadDeclarationFile.rejected, (state, action) => {
        state.downloading = false;
        state.downloadingError = parseServerError(action.error);
      })
      .addCase(downloadMemoFile.pending, (state) => {
        state.downloadingMemo = true;
      })
      .addCase(downloadMemoFile.fulfilled, (state, action) => {
        state.downloadingMemo = false;
        state.downloadingMemoError = null;
        state.toDownloadMemo = { ...action.payload };
      })
      .addCase(downloadMemoFile.rejected, (state, action) => {
        state.downloadingMemo = false;
        state.downloadingMemoError = parseServerError(action.error);
      })
      .addCase(createDeclaration.pending, (state) => {
        state.creating = true;
      })
      .addCase(createDeclaration.fulfilled, (state, action) => {
        state.creating = false;
        state.creatingError = null;
        state.creatingVatReturn = action.payload
      })
      .addCase(createDeclaration.rejected, (state, action) => {
        state.creating = false;
        const payload = action.payload;
        state.creatingError =
          (payload && (payload.error || payload.message)) ||
          parseServerError(action.error);
      })
      .addCase(submitDeclaration.pending, (state) => {
        state.submitting = true;
      })
      .addCase(submitDeclaration.fulfilled, (state) => {
        state.submitting = false;
        state.submittingError = null;
      })
      .addCase(submitDeclaration.rejected, (state, action) => {
        state.submitting = false;
        state.submittingError = parseServerError(action.payload.error);
      })
      .addCase(getReturnPreview.pending, (state) => {
        state.getting = true;
      })
      .addCase(getReturnPreview.fulfilled, (state, action) => {
        state.getting = false;
        state.gettingError = null;
        state.returnPreview = { ...action.payload };
      })
      .addCase(getReturnPreview.rejected, (state, action) => {
        state.getting = false;
        state.gettingError = parseServerError(action.error);
      })
      .addCase(getLiabilities.pending, (state) => {
        state.ordering = true;
      })
      .addCase(getLiabilities.fulfilled, (state, action) => {
        state.ordering = false;
        state.orderingError = null;
        state.liabilities = { ...action.payload };
      })
      .addCase(getLiabilities.rejected, (state, action) => {
        state.ordering = false;
        state.orderingError = parseServerError(action.error);
      })
      .addCase(getObligations.pending, (state) => {
        state.ordering = true;
      })
      .addCase(getObligations.fulfilled, (state, action) => {
        state.ordering = false;
        state.orderingError = null;
        state.liabilitiesAndObligations = { ...action.payload };
      })
      .addCase(getObligations.rejected, (state, action) => {
        state.ordering = false;
        state.orderingError = parseServerError(action.error);
      })
      .addCase(fetchTypesOfReport.pending, (state) => {
        state.fetchingTypes = true;
      })
      .addCase(fetchTypesOfReport.fulfilled, (state, action) => {
        state.fetchingTypes = false;
        state.fetchingTypesError = null;
        state.typesOfReport = action.payload;
      })
      .addCase(fetchTypesOfReport.rejected, (state, action) => {
        state.fetchingTypes = false;
        state.fetchingTypesError = parseServerError(action.error);
      })
      .addCase(deleteDeclaration.pending, (state) => {
        state.deleting = true;
        state.deletingError = null;
      })
      .addCase(deleteDeclaration.fulfilled, (state) => {
        state.deleting = false;
        state.deletingError = null;
      })
      .addCase(deleteDeclaration.rejected, (state, action) => {
        state.deleting = false;
        state.deletingError = parseServerError(action.error);
      })
      .addCase(checkVatNumbers.pending, (state) => {
        state.checking = true;
      })
      .addCase(checkVatNumbers.fulfilled, (state, action) => {
        state.checking = false;
        state.checkingError = null;
        state.checkedNumbers = { ...action.payload };
      })
      .addCase(checkVatNumbers.rejected, (state, action) => {
        state.checking = false;
        state.checkingError = parseServerError(action.error);
      })
      .addCase(fetchDeclarationsSalesTax.pending, (state) => {
        state.fetchingSalesTax = true;
      })
      .addCase(fetchDeclarationsSalesTax.fulfilled, (state, action) => {
        state.fetchingSalesTax = false;
        state.fetchingSalesTaxError = null;
        state.declarationsSalesTax = { ...action.payload };
      })
      .addCase(fetchDeclarationsSalesTax.rejected, (state, action) => {
        state.fetchingSalesTax = false;
        state.fetchingSalesTaxError = parseServerError(action.error);
      })
      .addCase(createDeclarationSalesTax.pending, (state) => {
        state.creatingSalesTax = true;
      })
      .addCase(createDeclarationSalesTax.fulfilled, (state, action) => {
        state.creatingSalesTax = false;
        state.creatingSalesTaxError = null;
        state.createdSalesTaxReturn = action.payload;
      })
      .addCase(createDeclarationSalesTax.rejected, (state, action) => {
        state.creatingSalesTax = false;
        // Don't set error for subscription-related responses (403 with subscription error)
        if (action.payload && typeof action.payload === 'object' && 
            (action.payload.to_buy || action.payload.message || 
             (action.payload.error && action.payload.error.includes('Subscription period not active')))) {
          // This is a subscription response, don't set error
          state.creatingSalesTaxError = null;
        } else {
          const serverError = action.error || 'Subscription period not active.'
          state.creatingSalesTaxError = parseServerError(serverError);
        }
      })
      .addCase(fetchRecalculatedSalesTaxData.pending, (state) => {
        state.fetchingRecalculatedData = true;
        state.fetchingRecalculatedDataError = null;
      })
      .addCase(fetchRecalculatedSalesTaxData.fulfilled, (state, action) => {
        state.fetchingRecalculatedData = false;
        state.fetchingRecalculatedDataError = null;
        state.recalculatedSalesTaxData = action.payload;
      })
      .addCase(fetchRecalculatedSalesTaxData.rejected, (state, action) => {
        state.fetchingRecalculatedData = false;
        state.fetchingRecalculatedDataError = parseServerError(action.error);
      })
      .addCase(deleteSalesTax.pending, (state) => {
        state.deletingTax = true;
        state.deletingTaxError = null;
      })
      .addCase(deleteSalesTax.fulfilled, (state) => {
        state.deletingTax = false;
        state.deletingTaxError = null;
      })
      .addCase(deleteSalesTax.rejected, (state, action) => {
        state.deletingTax = false;
        state.deletingTaxError = parseServerError(action.error);
      })
      .addCase(downloadSchedulesFile.pending, (state) => {
        state.downloadingSched = true;
      })
      .addCase(downloadSchedulesFile.fulfilled, (state, action) => {
        state.downloadingSched = false;
        state.downloadingSchedError = null;
        state.toDownloadSched = { ...action.payload };
      })
      .addCase(downloadSchedulesFile.rejected, (state, action) => {
        state.downloadingSched = false;
        state.downloadingSchedError =
          typeof action.payload === "string"
            ? action.payload
            : parseServerError(action.error);
      })
      .addCase(getSalesTaxPreview.pending, (state) => {
        state.gettingSalesTax = true;
      })
      .addCase(getSalesTaxPreview.fulfilled, (state, action) => {
        state.gettingSalesTax = false;
        state.gettingSalesTaxError = null;
        state.returnSalesTaxPreview = { ...action.payload };
      })
      .addCase(getSalesTaxPreview.rejected, (state, action) => {
        state.gettingSalesTax = false;
        state.gettingSalesTaxError = parseServerError(action.error);
      })
      .addCase(fetchSalesTaxPermits.pending, (state) => {
        state.fetchingTaxPermits = true;
      })
      .addCase(fetchSalesTaxPermits.fulfilled, (state, action) => {
        state.fetchingTaxPermits = false;
        state.fetchingTaxPermitsError = null;
        state.taxPermits = action.payload;
      })
      .addCase(fetchSalesTaxPermits.rejected, (state, action) => {
        state.fetchingTaxPermits = false;
        state.fetchingTaxPermitsError = parseServerError(action.error);
        state.taxPermits = [];
      })
      .addCase(submitSalesTax.pending, (state) => {
        state.submitingSalesTax = true;
      })
      .addCase(submitSalesTax.fulfilled, (state, action) => {
        state.submitingSalesTax = false;
        state.submitingSalesTaxError = null;
        state.submittedSalesTax = { ...action.payload };
      })
      .addCase(submitSalesTax.rejected, (state, action) => {
        state.submitingSalesTax = false;
        state.submitingSalesTaxError = parseServerError(action.error);
      })
      .addCase(createSystemOperator.pending, (state) => {
        state.creatingOperator = true;
      })
      .addCase(createSystemOperator.fulfilled, (state) => {
        state.creatingOperator = false;
        state.creatingOperatorError = null;
      })
      .addCase(createSystemOperator.rejected, (state, action) => {
        state.creatingOperator = false;
        state.creatingOperatorError = parseServerError(action.error);
      })
      .addCase(createEPRSettings.pending, (state) => {
        state.creatingEPRSettings = true;
      })
      .addCase(createEPRSettings.fulfilled, (state, action) => {
        state.creatingEPRSettings = false;
        state.creatingEPRSettingsError = null;
        const payload = action.payload;
        const arg = action.meta.arg || {};
        const display = payload?._display || arg._display;
        const fromApi = payload && typeof payload === "object" && (payload.id != null || payload.registration_number != null);
        const itemFromApi = fromApi ? payload : null;
        const itemFromData = payload?.data && typeof payload.data === "object" && (payload.data.id != null || payload.data.registration_number != null) ? payload.data : null;
        const itemFromResult = payload?.result && typeof payload.result === "object" && (payload.result.id != null || payload.result.registration_number != null) ? payload.result : null;
        const apiItem = itemFromApi || itemFromData || itemFromResult;
        if (apiItem) {
          const { _display: _omit, ...apiItemClean } = apiItem;
          const status = resolveEprStatus(
            apiItem.status ?? apiItem.registration_status ?? arg.status
          );
          const activate = resolveEprActivate(
            apiItem.activate ?? arg.activate,
            status
          );
          const newItem = {
            ...apiItemClean,
            activate,
            status,
          };
          state.listEPR = [newItem, ...(state.listEPR || [])];
        } else if ((payload?.id != null || payload?.data?.id != null || payload?.result?.id != null || arg.registration_number != null) && display) {
          const serverId = payload?.id ?? payload?.data?.id ?? payload?.result?.id;
          const status = resolveEprStatus(
            payload?.status ??
              payload?.data?.status ??
              payload?.result?.status ??
              arg.status
          );
          const activate = resolveEprActivate(arg.activate, status);
          const newItem = {
            id: serverId ?? `new-${Date.now()}`,
            registration_number: payload?.registration_number ?? payload?.data?.registration_number ?? payload?.result?.registration_number ?? arg.registration_number ?? "",
            country: payload?.country ?? payload?.data?.country ?? display.country ?? "",
            type_of_registration: payload?.type_of_registration ?? payload?.data?.type_of_registration ?? display.type_of_registration ?? "",
            system_operator: payload?.system_operator ?? payload?.data?.system_operator ?? display.system_operator ?? "",
            registration_date: payload?.registration_date ?? payload?.data?.registration_date ?? arg.registration_date ?? "",
            activate,
            status,
            vat_agent: arg.vat_agent_id === 2 || arg.epr_agent_id === 2 ? "Lappa" : null,
            vat_agent_id: arg.vat_agent_id ?? (arg.epr_agent_id === 2 ? 2 : null),
            state: payload?.state ?? payload?.data?.state ?? arg.state ?? "",
          };
          state.listEPR = [newItem, ...(state.listEPR || [])];
        }
      })
      .addCase(createEPRSettings.rejected, (state, action) => {
        state.creatingEPRSettings = false;
        state.creatingEPRSettingsError = parseServerError(action.error);
      })
      .addCase(fetchListEPR.pending, (state) => {
        state.fetchingListEPR = true;
      })
      .addCase(fetchListEPR.fulfilled, (state, action) => {
        state.fetchingListEPR = false;
        state.fetchingListEPRError = null;
        state.listEPR = action.payload;
      })
      .addCase(fetchListEPR.rejected, (state, action) => {
        state.fetchingListEPR = false;
        state.fetchingListEPRError = parseServerError(action.error);
      })
      .addCase(editActivateEpr.fulfilled, (state, action) => {
        const { id, activate } = action.meta.arg || {};
        if (id == null) return;
        const index = state.listEPR.findIndex((item) => item.id === id);
        if (index !== -1) {
          const status = resolveEprStatus(activate ? "active" : "inactive");
          state.listEPR[index] = { ...state.listEPR[index], activate, status };
        }
      })
      .addCase(fetchFiltersEPR.pending, (state) => {
        state.fetchingFiltersEPR = true;
      })
      .addCase(fetchFiltersEPR.fulfilled, (state, action) => {
        state.fetchingFiltersEPR = false;
        state.fetchingFiltersEPRError = null;
        state.filtersEPR = action.payload;
      })
      .addCase(fetchFiltersEPR.rejected, (state, action) => {
        state.fetchingFiltersEPR = false;
        state.fetchingFiltersEPRError = parseServerError(action.error);
      })
      .addCase(fetchEPRCategories.pending, (state) => {
        state.fetchingEPRParams = true;
      })
      .addCase(fetchEPRCategories.fulfilled, (state, action) => {
        state.fetchingEPRParams = false;
        state.fetchingEPRParamsError = null;
        state.eprCategories = action.payload;
      })
      .addCase(fetchEPRCategories.rejected, (state, action) => {
        state.fetchingEPRParams = false;
        state.fetchingEPRParamsError = parseServerError(action.error);
      })
      .addCase(fetchEPROperators.pending, (state) => {
        state.fetchingEPRParams = true;
      })
      .addCase(fetchEPROperators.fulfilled, (state, action) => {
        state.fetchingEPRParams = false;
        state.fetchingEPRParamsError = null;
        state.eprOperators = action.payload;
      })
      .addCase(fetchEPROperators.rejected, (state, action) => {
        state.fetchingEPRParams = false;
        state.fetchingEPRParamsError = parseServerError(action.error);
      })
      .addCase(createEprDeclaration.pending, (state) => {
        state.fetchingEPRParams = true;
      })
      .addCase(createEprDeclaration.fulfilled, (state, action) => {
        state.fetchingEPRParams = false;
        state.fetchingEPRParamsError = null;
        const request = action.meta.arg;
        const wizardFinalize =
          request && (request.submit === true || request.save === true);
        // Keep wizard form data until user dismisses the success modal; full reset runs on OK.
        if (!wizardFinalize) {
          state.eprDeclaration = action.payload;
        }
        const payload = action.payload;
        const unitsFromPayload =
          payload?.subcategories_data?.units ??
          payload?.subcategories_data_new?.units;
        if (unitsFromPayload) state.eprSubcategories.unitsDefault = unitsFromPayload;
      })
      .addCase(createEprDeclaration.rejected, (state, action) => {
        state.fetchingEPRParams = false;
        state.fetchingEPRParamsError = parseServerError(action.error);
      })
      .addCase(fetchEprFurnitureCategories.pending, (state) => {
        state.fetchingEprFurnitureCategories = true;
      })
      .addCase(fetchEprFurnitureCategories.fulfilled, (state, action) => {
        state.fetchingEprFurnitureCategories = false;
        state.eprFurniture.categories = action.payload;
      })
      .addCase(fetchEprFurnitureCategories.rejected, (state) => {
        state.fetchingEprFurnitureCategories = false;
      })
      .addCase(fetchEprFurnitureProducts.pending, (state) => {
        state.fetchingEprFurnitureProducts = true;
      })
      .addCase(fetchEprFurnitureProducts.fulfilled, (state, action) => {
        state.fetchingEprFurnitureProducts = false;
        state.eprFurniture.products = action.payload;
      })
      .addCase(fetchEprFurnitureProducts.rejected, (state) => {
        state.fetchingEprFurnitureProducts = false;
      })
      .addCase(fetchEprFurnitureMaterials.pending, (state) => {
        state.fetchingEprFurnitureMaterials = true;
      })
      .addCase(fetchEprFurnitureMaterials.fulfilled, (state, action) => {
        state.fetchingEprFurnitureMaterials = false;
        state.eprFurniture.materials = action.payload;
      })
      .addCase(fetchEprFurnitureMaterials.rejected, (state) => {
        state.fetchingEprFurnitureMaterials = false;
      })
      .addCase(fetchEprFurnitureFeatures.pending, (state) => {
        state.fetchingEprFurnitureFeatures = true;
      })
      .addCase(fetchEprFurnitureFeatures.fulfilled, (state, action) => {
        state.fetchingEprFurnitureFeatures = false;
        state.eprFurniture.features = action.payload;
      })
      .addCase(fetchEprFurnitureFeatures.rejected, (state) => {
        state.fetchingEprFurnitureFeatures = false;
      })
      .addCase(createEprFurnitureEcoCode.pending, (state) => {
        state.creatingEprFurnitureEcoCode = true;
      })
      .addCase(createEprFurnitureEcoCode.fulfilled, (state) => {
        state.creatingEprFurnitureEcoCode = false;
        // state.eprFurniture = {
        //   categories: null,
        //   products: null,
        //   materials: null,
        //   features: null,
        // };
      })
      .addCase(createEprFurnitureEcoCode.rejected, (state, action) => {
        state.creatingEprFurnitureEcoCode = false;
        state.creatingEprFurnitureEcoCodeError = parseServerError(action.error);
      })
      .addCase(editTransaction.pending, (state) => {
        state.isEditingTransaction = true;
      })
      .addCase(editTransaction.fulfilled, (state) => {
        state.isEditingTransaction = false;
      })
      .addCase(editTransaction.rejected, (state) => {
        state.isEditingTransaction = false;
        state.editingTransactionError = true;
      })
      .addCase(fetchEprCountries.pending, (state) => {
        state.isFetchingEprCounties = true;
      })
      .addCase(fetchEprCountries.fulfilled, (state, action) => {
        state.eprCountries = action.payload;
        state.isFetchingEprCounties = false;
      })
      .addCase(fetchEprCountries.rejected, (state, action) => {
        state.isFetchingEprCounties = false;
        state.fetchingEprCountriesError = parseServerError(action.error);
      })
      .addCase(fetchEprDeclarations.pending, (state) => {
        state.fetchingEprDeclarations = true;
      })
      .addCase(fetchEprDeclarations.fulfilled, (state, action) => {
        state.fetchingEprDeclarations = false;
        state.declarationsEpr = { ...action.payload };
      })
      .addCase(fetchEprDeclarations.rejected, (state, action) => {
        state.fetchingEprDeclarations = false;
        state.fetchingEprDeclarationsError = parseServerError(action.error);
      })
      .addCase(downloadEprFile.pending, (state) => {
        state.downloadingEprFile = true;
      })
      .addCase(downloadEprFile.fulfilled, (state) => {
        state.downloadingEprFile = false;
      })
      .addCase(downloadEprFile.rejected, (state, action) => {
        state.downloadingEprFile = false;
        state.downloadingEprFileError = parseServerError(action.error);
      })
      .addCase(submitEprReport.pending, (state) => {
        state.submittingEprReport = true;
      })
      .addCase(submitEprReport.fulfilled, (state) => {
        state.submittingEprReport = false;
      })
      .addCase(submitEprReport.rejected, (state, action) => {
        state.submittingEprReport = false;
        const payload = action.payload;
        state.submittingEprReportError =
          (payload && (payload.error || payload.message)) ||
          parseServerError(action.error);
      })
      .addCase(createDac7Report.pending, (state) => {
        state.creatingDac7Report = true;
      })
      .addCase(createDac7Report.fulfilled, (state, action) => {
        state.creatingDac7Report = false;
        state.dac7 = action.payload.merchants || [];
        state.business_name = action.payload.business_name;
        state.period = action.payload.period;
        state.dac7_summary = action.payload.data;
        state.user_status = action.payload.user_status ?? [];
        state.property_types = action.payload.property_types ?? [];
        state.relevant_activities = action.payload.relevant_activities ?? [];
        state.dac7_files_examples = action.payload.files;
        const reportId =
          action.payload?.id ??
          action.payload?.report_id ??
          action.payload?.dac7_id ??
          action.payload?.dac7_report_id;
        if (reportId != null) {
          state.dac7ReportId = reportId;
        }
      })
      .addCase(createDac7Report.rejected, (state, action) => {
        state.creatingDac7Report = false;
        state.creatingDac7ReportError = parseServerError(action.payload?.error);
      })
      .addCase(sendDac7Report.pending, (state) => {
        state.sendDac7Report = true;
      })
      .addCase(sendDac7Report.fulfilled, (state, action) => {
        state.sendDac7Report = false;
        const reportId = action.payload?.id ?? action.payload?.report_id;
        if (reportId != null) {
          state.dac7ReportId = reportId;
        }
      })
      .addCase(sendDac7Report.rejected, (state, action) => {
        state.sendDac7Report = false;
        state.sendDac7ReportError = parseServerError(action.error);
      })
      .addCase(editMerchantInfo.pending, (state) => {
        state.editingMerchantInfo = true;
      })
      .addCase(editMerchantInfo.fulfilled, (state, action) => {
        state.editingMerchantInfo = false;
        state.dac7 = state.dac7.map((item) => {
          if (item.id === action.payload.id) {
            return action.payload;
          }
          return item;
        });
      })
      .addCase(editMerchantInfo.rejected, (state, action) => {
        state.editingMerchantInfo = false;
        state.editingMerchantInfoError = parseServerError(action.error);
      })
      .addCase(fetchDac7Reports.pending, (state) => {
        state.fetchingDac7Reports = true;
      })
      .addCase(fetchDac7Reports.fulfilled, (state, action) => {
        state.fetchingDac7Reports = false;
        if (action.payload.is_immovable) {
          state.dac7ReportsImmovable = action.payload.items;
        } else {
          state.dac7Reports = action.payload.items;
        }
      })
      .addCase(fetchDac7Reports.rejected, (state, action) => {
        state.fetchingDac7Reports = false;
        state.fetchingDac7ReportsError = parseServerError(action.error);
      })
      .addCase(create1099Report.pending, (state) => {
        state.creating1099Report = true;
        state.creating1099ReportError = null;
      })
      .addCase(create1099Report.fulfilled, (state, action) => {
        state.creating1099Report = false;
        state.creating1099ReportError = null;
        state.report1099 = action.payload;
      })
      .addCase(create1099Report.rejected, (state, action) => {
        state.creating1099Report = false;
        state.creating1099ReportError = parseServerError(action.error);
      })
      .addCase(addMerchantDac7.pending, (state) => {
        state.addingMerchant = true;
        state.addingMerchantError = null;
      })
      .addCase(addMerchantDac7.fulfilled, (state, action) => {
        state.addingMerchant = false;
        state.addingMerchantError = null;
        state.dac7 = [...state.dac7, action.payload];
      })
      .addCase(addMerchantDac7.rejected, (state, action) => {
        state.addingMerchant = false;
        state.addingMerchantError = parseServerError(action.error);
      })
      .addCase(getDac7Preview.pending, (state) => {
        state.loadingDac7Preview = true;
      })
      .addCase(getDac7Preview.fulfilled, (state, action) => {
        state.loadingDac7Preview = false;
        state.dac7Preview = action.payload;
      })
      .addCase(getDac7Preview.rejected, (state, action) => {
        state.loadingDac7Preview = false;
        state.dac7PreviewError = parseServerError(action.error);
      })
      .addCase(sendEprPaymentMemo.pending, (state) => {
        state.uploadingPaymentMemo = true;
        state.uploadingPaymentMemoError = null;
      })
      .addCase(sendEprPaymentMemo.fulfilled, (state) => {
        state.uploadingPaymentMemo = false;
        state.uploadingPaymentMemoError = null;
      })
      .addCase(sendEprPaymentMemo.rejected, (state, action) => {
        state.uploadingPaymentMemo = false;
        state.uploadingPaymentMemoError = parseServerError(action.error);
      })
      .addCase(getDeclarationTransaction.pending, (state) => {
        state.loadingDeclarationTransaction = true;
      })
      .addCase(getDeclarationTransaction.fulfilled, (state, action) => {
        state.loadingDeclarationTransaction = false;
        state.declarationTransactionError = null;
        state.declarationsTransactions = { ...action.payload }
      })
      .addCase(getDeclarationTransaction.rejected, (state, action) => {
        state.loadingDeclarationTransaction = false;
        state.declarationTransactionError = parseServerError(action.error);
      })
      .addCase(getSalesTaxReturnTransactions.pending, (state) => {
        state.loadingSalesTaxReturnTransaction = true;
        state.salesTaxReturnTransactionError = null;
      })
      .addCase(getSalesTaxReturnTransactions.fulfilled, (state, action) => {
        state.loadingSalesTaxReturnTransaction = false;
        const p = action.payload;
        const items = Array.isArray(p)
          ? p
          : p?.items ?? p?.results ?? [];
        const count =
          typeof p?.count === "number"
            ? p.count
            : typeof p?.total === "number"
              ? p.total
              : items.length;
        state.salesTaxReturnTransactions = { ...p, items, count };
      })
      .addCase(getSalesTaxReturnTransactions.rejected, (state, action) => {
        state.loadingSalesTaxReturnTransaction = false;
        state.salesTaxReturnTransactionError = parseServerError(action.error);
      })
      .addCase(createSalesTaxReturnTransaction.pending, (state) => {
        state.creatingSalesTaxReturnTransaction = true;
        state.createSalesTaxReturnTransactionError = null;
      })
      .addCase(createSalesTaxReturnTransaction.fulfilled, (state) => {
        state.creatingSalesTaxReturnTransaction = false;
      })
      .addCase(createSalesTaxReturnTransaction.rejected, (state, action) => {
        state.creatingSalesTaxReturnTransaction = false;
        state.createSalesTaxReturnTransactionError = parseServerError(
          action.error
        );
      })
      .addCase(voidSalesTaxReturnTransaction.pending, (state) => {
        state.voidingSalesTaxReturnTransaction = true;
        state.voidSalesTaxReturnTransactionError = null;
      })
      .addCase(voidSalesTaxReturnTransaction.fulfilled, (state) => {
        state.voidingSalesTaxReturnTransaction = false;
      })
      .addCase(voidSalesTaxReturnTransaction.rejected, (state, action) => {
        state.voidingSalesTaxReturnTransaction = false;
        state.voidSalesTaxReturnTransactionError = parseServerError(
          action.error
        );
      })
      .addCase(recalculateSalesTaxReturnTransactions.pending, (state) => {
        state.recalculatingSalesTaxReturnTransactions = true;
        state.recalculateSalesTaxReturnTransactionsError = null;
      })
      .addCase(recalculateSalesTaxReturnTransactions.fulfilled, (state) => {
        state.recalculatingSalesTaxReturnTransactions = false;
      })
      .addCase(recalculateSalesTaxReturnTransactions.rejected, (state, action) => {
        state.recalculatingSalesTaxReturnTransactions = false;
        state.recalculateSalesTaxReturnTransactionsError =
          action.payload != null
            ? String(action.payload)
            : parseServerError(action.error);
      })
      .addCase(getReturnsTransactions.pending, (state) => {
        state.loadingReturnsTransactions = true;
        state.returnsTransactionsError = null;
      })
      .addCase(getReturnsTransactions.fulfilled, (state, action) => {
        state.loadingReturnsTransactions = false;
        state.returnsTransactions = normalizeReturnsTransactionsPayload(
          action.payload
        );
      })
      .addCase(getReturnsTransactions.rejected, (state, action) => {
        state.loadingReturnsTransactions = false;
        state.returnsTransactionsError = parseServerError(action.error);
      })
      .addCase(createReturnsTransaction.pending, (state) => {
        state.creatingReturnsTransaction = true;
        state.createReturnsTransactionError = null;
      })
      .addCase(createReturnsTransaction.fulfilled, (state) => {
        state.creatingReturnsTransaction = false;
      })
      .addCase(createReturnsTransaction.rejected, (state, action) => {
        state.creatingReturnsTransaction = false;
        state.createReturnsTransactionError = parseServerError(action.error);
      })
      .addCase(editReturnsTransaction.pending, (state) => {
        state.editingReturnsTransaction = true;
        state.editReturnsTransactionError = null;
      })
      .addCase(editReturnsTransaction.fulfilled, (state) => {
        state.editingReturnsTransaction = false;
      })
      .addCase(editReturnsTransaction.rejected, (state, action) => {
        state.editingReturnsTransaction = false;
        state.editReturnsTransactionError = parseServerError(action.error);
      })
      .addCase(recalculateReturnsTransactions.pending, (state) => {
        state.recalculatingReturnsTransactions = true;
        state.loadingReturnsTransactions = true;
        state.recalculateReturnsTransactionsError = null;
        state.returnsTransactionsError = null;
      })
      .addCase(recalculateReturnsTransactions.fulfilled, (state, action) => {
        state.recalculatingReturnsTransactions = false;
        state.loadingReturnsTransactions = false;
        state.returnsTransactions = normalizeReturnsTransactionsPayload(
          action.payload
        );
      })
      .addCase(recalculateReturnsTransactions.rejected, (state, action) => {
        state.recalculatingReturnsTransactions = false;
        state.loadingReturnsTransactions = false;
        state.recalculateReturnsTransactionsError =
          action.payload != null
            ? String(action.payload)
            : parseServerError(action.error);
        state.returnsTransactionsError =
          state.recalculateReturnsTransactionsError;
      })
      .addCase(fetchEprStatus.pending, (state) => {
        state.fetchEprStatus = true;
      })
      .addCase(fetchEprStatus.fulfilled, (state, action) => {
        state.fetchEprStatus = false;
        state.fetchEprStatusError = null;
        state.statuses = action.payload
      })
      .addCase(fetchEprStatus.rejected, (state, action) => {
        state.fetchEprStatus = false;
        state.fetchEprStatusError = parseServerError(action.error);
      })
      .addCase(uploadMerchantsFileEprReporting.pending, (state) => {
        state.uploadMerchantsFileEprReportingLoading = true;
      })
      .addCase(uploadMerchantsFileEprReporting.fulfilled, (state, action) => {
        state.uploadMerchantsFileEprReportingLoading = false;
        state.uploadMerchantsFileEprReportingError = null;
        // console.log('action ---------', action.payload)
      })
      .addCase(uploadMerchantsFileEprReporting.rejected, (state, action) => {
        state.uploadMerchantsFileEprReportingLoading = false;
        state.uploadMerchantsFileEprReportingError = parseServerError(action.error.message);
      });
  },
});

export const {
  selectCategory1,
  selectCategory2,
  selectCategory3,
  selectCategory4,
  selectCategory5,
  selectCategory6,
  selectCategory7,
  selectCategory8,
  setQuantity,
  setVatReturnsData,
  setResetData,
  setSelectedCategoriesFromNewArch,
  setQuantityForTwoSteps,
  setQuantityFor7StepsNLD,
  setResetEprDeclaration,
  setDataForDac7Report,
  setDac7ReportId,
  setDataCreateEprDeclaration,
  setQuantityArr,
  setQuantityManyUnitsAmount,
  setChangeVariantCreatedDeclarationEpr,
  // New isolated actions for NLD Batteries with objects
  createNldBatteriesItems,
  updateNldBatteriesSubcategory4,
  updateNldBatteriesSubcategory5,
  updateNldBatteriesSubcategory6,
  updateNldBatteriesSubcategory7,
  updateNldBatteriesQuantity,
  resetNldBatteriesItems,
  clearSubmittingEprReportError,
  clearCreateReturnsTransactionError,
  clearCreateSalesTaxReturnTransactionError,
} = declarationsSlice.actions;

export default declarationsSlice.reducer;
